import {
	ArrowsClockwise,
	Files,
	Receipt,
	Sparkle,
	WhatsappLogo,
} from '@phosphor-icons/react';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import {
	ADMATTIC_ROLE_ANSWER_KEY,
	AG_ROLE_ANSWER_KEY,
	ALLY_ANSWER_KEY,
	ALLY_ROLE_ANSWER_KEYS,
	CALCUMX_ROLE_ANSWER_KEY,
	TUAL_ROLE_ANSWER_KEY,
	PLATA_ROLE_ANSWER_KEY,
	DIDI_INTENT_ANSWER_KEY,
	DIGITALHUB_ROLE_ANSWER_KEY,
	MASLOW_ROLE_ANSWER_KEY,
	MORADA_ROLE_ANSWER_KEY,
	type AnswerValue,
	type PartnerLogoKey,
} from '../model/flowSchema';
import { isAllyId, type AllyId } from '../model/allyIds';
import didiTutorialAvatar from '@/assets/partners/aliados/didi-tutorial-mich.webp';
import didiTutorialPoster from '@/assets/partners/aliados/didi-tutorial-cover.webp';
import didiTutorialVideo from '@/assets/partners/aliados/didi-tutorial-mich.mp4';
import { LEGAL_DOCS } from '@/shared/legal/legalDocs';

/**
 * Catálogo de PRESENTACIÓN — copy, marca, planes mensuales y content packs del
 * onboarding. `MONTHLY_PLANS` centraliza los tres planes mensuales (paridad de
 * familias con `PLAN_CATALOG`/`RECOMMENDED_PLANS` de shared/plans) y lo consumen
 * tanto el branch de aliados como los terminales de plan de known-regime/
 * accounting. `ALLY_CATALOG` es el refactor puro del `ALLIES` de v2
 * (features/onboarding/constants/allyFlow.ts): las llaves son los valores que
 * guarda el grid (`answers[ALLY_ANSWER_KEY]`); la paridad flow ⇄ catálogo la
 * exige allyCatalog.test.ts en CI.
 *
 * Cada aliado trae su `content` pack por rol (la capa de capacidades que pintan
 * AllyCapabilitiesScreen y el recap de AllyPlanScreen). Es DATA pura re-expresada
 * del content de v2, NO importada — allyFlow.ts nunca cruza a las pantallas puras.
 *
 * Fase 1 (fuera de estas pantallas puras):
 *  - Los precios de MONTHLY_PLANS son representativos; en producción salen del
 *    sales catalog (useCatalog('subscriptions')), que además reintroduce el
 *    framing de descuento (tachado/ahorro anual) que aquí no tiene fuente.
 *  - El mapeo aliado → organización backend (org-linking con el email como
 *    org_code) lo ejecuta el host al recibir el effect 'ally-org-link'.
 */

// Familias de plan mensual — mismo union que `PlanFamily` de shared/plans; se
// replica aquí para no importar fuera de la allowlist de pantallas puras.
export type MonthlyPlanId = 'plataformas' | 'profesionista' | 'empresarial';

export type MonthlyPlan = {
	name: string;
	/** El nombre que cabe en una columna angosta (la tabla de precios de la
	 * landing de un aliado, tres planes lado a lado en un celular). */
	shortName: string;
	tagline: string;
	priceMonthlyCents: number;
	/** Precio de LISTA del producto anual (un pago al año). Es su propio punto
	 * de precio, más barato que 12 mensualidades: el anual de un aliado se
	 * calcula sobre este número, no multiplicando el mensual. */
	priceAnnualCents: number;
	benefits: string[];
};

export const MONTHLY_PLANS: Record<MonthlyPlanId, MonthlyPlan> = {
	plataformas: {
		name: 'Plan Plataformas Digitales',
		shortName: 'Plataformas',
		tagline: 'Diseñado para socios de plataformas tecnológicas',
		priceMonthlyCents: 24900,
		priceAnnualCents: 224400,
		benefits: [
			'Declaramos tus impuestos de plataformas cada mes',
			'Detectamos tus deducciones automáticamente',
			'Facturación CFDI desde la app',
			'Soporte fiscal por chat cuando lo necesites',
		],
	},
	profesionista: {
		name: 'Plan RESICO',
		shortName: 'RESICO',
		tagline: 'Para quien factura desde varios regímenes',
		priceMonthlyCents: 39900,
		priceAnnualCents: 358800,
		benefits: [
			'Presentamos tus declaraciones mensuales por ti',
			'Un contador concilia tus ingresos y declara cada régimen',
			'Facturación CFDI desde la app',
			'Soporte fiscal por chat cuando lo necesites',
		],
	},
	empresarial: {
		name: 'Actividad Empresarial Plus',
		shortName: 'Empresarial',
		tagline: 'Para Actividad Empresarial o RIF',
		priceMonthlyCents: 59900,
		priceAnnualCents: 538800,
		benefits: [
			'Presentamos tus declaraciones mensuales de Actividad Empresarial o RIF',
			'Un contador calcula tus utilidades y concilia tus gastos deducibles',
			'Facturación CFDI desde la app',
			'Soporte fiscal por chat cuando lo necesites',
		],
	},
};

/** Un beneficio del plan dicho con punch — capacidad real que el usuario da por
 * sentada. El icono es un componente de @phosphor-icons/react (la card lo pinta
 * en un chip tintado con el hex de marca). */
export type PunchBenefit = { icon: PhosphorIcon; text: string };

/** Cola compartida de alto valor de TODOS los planes (misma en las tres
 * familias); cada familia antepone su apertura de declaraciones. */
const SHARED_PUNCH_BENEFITS: PunchBenefit[] = [
	{ icon: Files, text: 'Factura desde la app, en segundos' },
	{ icon: WhatsappLogo, text: 'Soporte fiscal experto por WhatsApp' },
	{ icon: ArrowsClockwise, text: 'Extracción diaria de tus facturas del SAT' },
	{ icon: Sparkle, text: 'Deducciones optimizadas con IA, sin costo adicional' },
];

/** Grid de beneficios de la card del plan de aliado, por familia de plan. NO es
 * MONTHLY_PLANS[].benefits (esa la consumen los terminales de known-regime y
 * accounting): esta es la voz "punch" del branch de aliados. */
export const PLAN_PUNCH_BENEFITS: Record<MonthlyPlanId, PunchBenefit[]> = {
	plataformas: [
		{
			icon: Receipt,
			text: 'Declaraciones de tus plataformas presentadas cada mes, antes del día 17',
		},
		...SHARED_PUNCH_BENEFITS,
	],
	profesionista: [
		{
			icon: Receipt,
			text: 'Tu declaración RESICO presentada cada mes por contadores expertos',
		},
		...SHARED_PUNCH_BENEFITS,
	],
	empresarial: [
		{
			icon: Receipt,
			text: 'Las declaraciones de tu actividad presentadas cada mes',
		},
		...SHARED_PUNCH_BENEFITS,
	],
};

/** Trial de campaña del plan de un aliado: el backend (plans service) extiende
 * period_end al leer `campaign` en additional_information del item. La card lo
 * pinta como framing (mes gratis mensual / 15 meses por 12 anual). */
export type AllyTrial = {
	campaign: string;
	monthlyFreeMonths: number;
	annualTotalMonths: number;
};

/** Icono de un módulo de capacidad — union CERRADO que mapea a componentes de
 * @phosphor-icons/react en el registry exhaustivo de CapabilityModuleCard; un
 * typo del catálogo falla el compile en vez de renderizar en blanco. */
export type AllyCapabilityIcon =
	| 'Receipt'
	| 'Files'
	| 'DeviceMobile'
	| 'ChartLineUp'
	| 'Headset'
	| 'CheckCircle';

/** Un módulo = una capacidad en la capa "cómo te ayuda heru": icono + título +
 * 1-2 líneas. `featured` resalta A LO MÁS un diferenciador real por rol (card
 * tintada de marca + pill EXCLUSIVO) — p.ej. la página de facturación personal. */
export type AllyCapabilityModule = {
	icon: AllyCapabilityIcon;
	title: string;
	copy: string;
	featured?: boolean;
};

/** Content pack por ROL: lo que la capa de capacidades varía según la actividad
 * económica del usuario. Los aliados de una sola actividad usan la llave
 * 'default'; Morada Uno llavea por su rol elegido (agente/propietario). Re-
 * expresado del AllyRoleContent de v2 SIN los campos que v3 ya cubre por otra
 * fuente (socioLabel → Ally.roleSocioLabels, planSubtitle → Ally.cardSubtitle,
 * heroSubject/heroImage → sin slot de persona en el hero v3). */
export type AllyRoleContent = {
	/** "Todo lo que heru hace por ti como {label}". */
	label: string;
	/** "Diseñado para tu actividad y régimen, {regime}". */
	regime: string;
	/** Línea co-branded bajo "heru × {aliado}" en el hero de la capa. */
	heroLine: string;
	/** Ordenados por valor de conversión (el primero = el gancho). 3-6 por rol. */
	modules: AllyCapabilityModule[];
};

/** Certificados que arma el fiscal-engine (kinds de POST /downloads/links). */
export type CertificateKind = 'zubale_certificate' | 'didi_certificate' | 'rappi_certificate';

/** Un renglón de «Así está tu perfil»: un requisito del aliado en una columna
 * ✓/✗ que, al tocarlo, explica de dónde sale el veredicto y ofrece la salida.
 * `verdict` es la llave de ctx con el veredicto de la validación por CURP
 * ('ok' · 'warn' · '' sin veredicto), o lo que ya está en heru
 * (`efirmaEnHeru` / `csdEnHeru`). `resolve` decide la salida:
 * - `efirma-sat`: sin e.firma vigente, la cita por WhatsApp; vigente, subirla.
 * - `efirma-heru`: con e.firma vigente, subirla; si no, la cita.
 * - `csd-sat`: con sellos activos, subirlos; si no, vía e.firma (abajo).
 * - `csd-heru`: con sellos activos, subirlos; si no, vía e.firma.
 * - `sat-update`: vía e.firma.
 * «Vía e.firma»: sin e.firma vigente, la cita; sin subirla, subirla; con ella
 * en heru, heru hace el trámite y el renglón muestra `notice`.
 * El copy con `{valor}` interpola `valueKey` (fecha o régimen) cuando llega. */
export type AllyProfileRequirement = {
	id: string;
	title: string;
	/** The row's title once it is met, when `title` asks for an action
	 * («Sube tu e.firma a heru» reads «Tu e.firma está en heru»). */
	doneTitle?: string;
	/** Por qué el aliado lo pide: el popover ⓘ junto al título del renglón. */
	why: string;
	verdict:
		| 'curpRfcStatus'
		| 'curpEfirmaStatus'
		| 'efirmaEnHeru'
		| 'curpCsdStatus'
		| 'csdEnHeru'
		| 'curpPlatecRegimeStatus'
		| 'repartoActivityStatus';
	valueKey?: 'curpRfc' | 'curpEfirmaValidTo' | 'curpCsdValidTo' | 'curpRegimesLabel';
	resolve: 'efirma-sat' | 'efirma-heru' | 'csd-sat' | 'csd-heru' | 'sat-update';
	okCopy: string;
	okCopyWithValue?: string;
	warnCopy: string;
	warnCopyWithValue?: string;
	/** Con el CURP validado pero sin veredicto para este requisito. */
	unknownCopy?: string;
	notice?: string;
};

/** Config del hub de cumplimiento del aliado (action 'ally-profile'): la
 * ESTRUCTURA del hub es una (AllyProfileScreen); aquí vive lo que varía por
 * aliado — copy, si exige CURP, verificaciones extra y si emite certificado. */
export type AllyComplianceHub = {
	/** Copy del hero del hub. */
	heroCopy: string;
	/** Título del card de pasos; `{total}` interpola el número de pasos. */
	stepsTitle: string;
	/** Nota bajo los pasos mientras falte alguno; `{total}` interpola. */
	completionNote: string;
	/** El CURP como PRIMER requisito del hub (DiDi). NO bloquea el resto: los
	 * demás pasos siguen accionables mientras el CURP se captura/valida. */
	curp?: {
		title: string;
		doneTitle: string;
		/** El porqué del CURP (estado accionable). */
		description: string;
		/** Título de «Así está tu perfil» y los requisitos del aliado, en orden. */
		profileTitle: string;
		profile: AllyProfileRequirement[];
		/** Título entre «Así está tu perfil» y las cards de pasos mientras falte
		 * alguno; `{n}` = pasos pendientes. */
		pendingStepsTitle: { one: string; other: string };
		/** Mensaje prellenado a la línea ASAT para agendar la cita de la e.firma.
		 * Ausente = el aliado no tiene soporte por WhatsApp (Zubale): el renglón
		 * explica cómo renovarla en vez de abrir el chat. */
		appointmentWhatsApp?: string;
	};
	/** Por qué el aliado necesita la e.firma (estado vinculado, pendiente). */
	efirmaWhy: string;
	/** Card de la e.firma cuando ya está activa pero el RFC o el régimen piden
	 * atención. Ausente = el copy genérico que lo resuelve con el plan. */
	efirmaAttention?: string;
	/** «¿Necesitas ayuda? Contáctanos»: mensaje prellenado a la línea ASAT.
	 * `{nombre}` = primer nombre y `{correo}` = el correo con el que se
	 * identificó con el aliado; sin nombre se usa `withoutName`. Ausente = el
	 * hub no ofrece el botón. */
	helpWhatsApp?: { withName: string; withoutName: string };
	/** Mensaje prellenado a la línea ASAT de WhatsApp cuando el socio no tiene
	 * su e.firma («Solicitar trámite con heru»). Ausente = ese botón lleva a los
	 * planes ASAT. */
	efirmaHelpWhatsApp?: string;
	/** Por qué el aliado necesita los sellos CSD. */
	sellosWhy: string;
	/** Cuerpo del banner "Tranquilo, heru se encarga" (visible al vincular). */
	allianceBanner: string;
	/** Certificado heru descargable al cumplir TODO (Zubale: las verificaciones
	 * fiscales; aliados con CURP: todos los requisitos de «Así está tu
	 * perfil»). Lo arma el servidor (`kind` de POST /downloads/links). Ausente =
	 * el hub no ofrece certificado. */
	certificate?: {
		kind: CertificateKind;
		fileName: string;
		readyTitle: string;
		readyBody: string;
		downloadLabel: string;
	};
	/** Cola del disclaimer legal, después del "heru no es el SAT…" compartido. */
	disclaimer: string;
	/** El veredicto del aliado manda (Zubale, t-338; Rappi, t-420): el slug del
	 * partner en worker-compliance (GET partner-compliance). Si su caso está
	 * `compliant` (lo que el aliado ve en su portal y reporte), el hub queda en
	 * orden completo, con todos los requisitos en verde y el certificado, sin
	 * importar la validación por CURP. Sin caso o `needs_work`, decide la
	 * validación por CURP. El servidor del certificado aplica la misma regla.
	 * Es independiente de `fiscalValidationPartner`: leer el veredicto no
	 * dispara validaciones pagadas. */
	partnerVerdictPartner?: string;
	/** El aliado no pide la e.firma (Rappi: RFC activo y sellos activos y en
	 * heru). El hub no la pone como paso, los sellos cuentan como hechos al
	 * estar en heru y el certificado sale de «Así está tu perfil» completo.
	 * Ausente = la e.firma es un paso del hub. */
	requiresEfirma?: false;
	/** El renglón de «Así está tu perfil» cuando manda el veredicto del aliado. */
	partnerVerdictCopy?: string;
	/** El paso de la e.firma de quien está en orden con el aliado pero ya no
	 * tiene su SAT vinculado: el certificado pide el vínculo activo. */
	partnerVerdictRelinkCopy?: string;
};

/** Capacidad de CUMPLIMIENTO del aliado: los aliados cuyo convenio incluye
 * activación/certificación fiscal (Zubale, DiDi) la declaran aquí y el flow los
 * rutea a las pantallas generalizadas (ally-tcs → ally-profile). Un aliado sin
 * `compliance` solo vende (capacidades → plan). Tercer partner = otra entrada
 * de DATA aquí + sus nodos en el flow JSON. */
export type AllyCompliance = {
	/** doc_type del anexo del aliado en compliance_document_service — la
	 * pantalla de T&Cs lo pinta y el gate BLOQUEANTE del host lo firma, ambos
	 * por la misma query (jamás hardcodear el documento en la pantalla).
	 * Ausente = el programa no tiene T&Cs (Rappi): su flow no lleva ally-tcs. */
	docType?: string;
	/** Título y cuerpo de la card de T&Cs cuando el anexo del aliado autoriza
	 * MÁS que compartir información (DiDi: trámites ante el SAT por cuenta del
	 * usuario). Ausente = copy genérico "Comparte tu información con {aliado}". */
	tcs?: { title: string; body: string };
	/** Promesa corta del programa bajo el eyebrow del login co-brandeado
	 * (DiDi pidió "sin costo para socios repartidores elegibles", 2026-09-10).
	 * Solo se pinta cuando el programa aplica al rol de la campaña. */
	loginTagline?: string;
	/** Identificación del usuario contra la LISTA del aliado (verify-and-claim
	 * de t-054), ejecutada por el gate 'ally-link-verify' del nodo de correo del
	 * aliado (`ally_email`): con `link` el correo tecleado se cruza contra la
	 * lista ANTES de avanzar; sin `link` el mismo nodo solo conecta la cuenta
	 * (Zubale). El tipo de key es configurable por aliado (hoy DiDi = correo);
	 * el copy del nodo y de cada rechazo vive aquí porque es la voz del aliado. */
	link?: AllyListLink;
	/** El programa es UNA de dos ofertas del aliado y el usuario ELIGE (DiDi:
	 * activación fiscal Last Mile vs. solo el plan de declaraciones). La
	 * respuesta vive en el nodo de ROL del aliado (`Ally.roleAnswerKey`): el
	 * programa —lista, T&Cs, hub, hito del home— aplica SOLO cuando la respuesta
	 * es `programChoice`; sin respuesta NO aplica (t-076 · 7: toda entrada a
	 * DiDi sin intención guardada pasa primero por la pregunta, venga del grid
	 * del onboarding, de «Aliados oficiales», del perfil o del hito del home).
	 * El flow rutea igual con visibleWhen `eq programChoice` en sus nodos de
	 * T&Cs/hub, y el deep-link `?partner=` pre-estampa `programChoice` sobre un
	 * nodo campaign-owned que por eso no se muestra (paridad en flowData.test).
	 * Ausente = el programa aplica a todo el que elige al aliado (Zubale). Toda
	 * lectura pasa por resolveAllyCompliance. */
	intent?: AllyComplianceIntent;
	/** Slug del partner b2b al que se ATRIBUYE la validación fiscal por CURP
	 * del programa (allowlist de `POST /user-account/web/partner-fiscal-validation`
	 * en user-management-engine; bps lo mapea a su partner_id: didi → 44, o el
	 * sandbox 45 por config del deploy). Con él, el CURP del hub dispara la FULL
	 * fiscal validation bajo el partner —la fila que enriquece el claim con RFC,
	 * lo lleva al roster como "RFC atendido" y se factura— en vez de la
	 * extracción sin partner del spine. Ausente = el CURP corre por
	 * `curp-extraction` a secas (Zubale: su validación es la extracción FIEL). */
	fiscalValidationPartner?: string;
	hub: AllyComplianceHub;
};

export type AllyComplianceIntent = {
	programChoice: string;
	planOnlyChoice: string;
};

/** Llave de answer del nodo de ROL de un aliado (miembro de
 * ALLY_ROLE_ANSWER_KEYS): la pregunta que el flow le hace SOLO a él después del
 * grid (morada_role, ag_role, admattic_role, digitalhub_role, didi_intent). */
export type AllyRoleAnswerKey = (typeof ALLY_ROLE_ANSWER_KEYS)[number];

export type AllyListLink = {
	/** Etiqueta del key pactada con el aliado (partner_link_config). */
	keyType: string;
	/** Título y cuerpo del nodo de correo cuando SÍ se cruza con la lista
	 * (reemplazan al copy genérico "Conecta tu cuenta" del flow). */
	title: string;
	body: string;
	/** Pill destacada sobre el título (DiDi: "Sin costo para ti"), para que la
	 * gratuidad del programa no viva solo en una frase del cuerpo. */
	badge?: string;
	notFound: string;
	alreadyClaimed: string;
	unavailable: string;
};

export type Ally = {
	id: AllyId;
	name: string;
	logo: PartnerLogoKey;
	/** Hex de marca: alimenta el gradiente del hero y el CTA branded. */
	bg: string;
	socioLabel: string;
	/** La plataforma de la que el socio cobra sus ingresos, como se nombra en el
	 * copy del plan Plataformas («…todas tus plataformas: Zubale, Uber, …»). Ausente
	 * = el aliado no es una plataforma de ingresos (una calculadora, una fintech,
	 * una renta de autos) y el copy queda con la lista genérica. */
	platformName?: string;
	/** Nodo de ROL propio del aliado: la llave que guarda la pregunta que el flow
	 * le hace SOLO a él tras el grid (Morada Uno: agente/propietario; AG,
	 * Admattic y Digital Hub: familia de plan; DiDi: servicio buscado). Es la
	 * ÚNICA llave que resolveAllyRoleAnswer lee para este aliado: el rol de otro
	 * aliado que quedó en el record (eligió Morada Uno, volvió atrás y eligió
	 * DiDi) jamás decide su hero, su etiqueta de socio, su plan ni si el
	 * programa de cumplimiento le aplica. La paridad catálogo ⇄ flow (el nodo
	 * que guarda esta llave está pinneado a este aliado, y solo a él) la exige
	 * flowData.test. Ausente = aliado sin rol (Uber, Rappi, Zubale). */
	roleAnswerKey?: AllyRoleAnswerKey;
	/** Etiqueta de socio por ROL (Morada Uno: agente/propietario) — el banner de
	 * confirmación del correo la resuelve con answers[roleAnswerKey] y cae a
	 * socioLabel (paridad con el ConfirmedBanner v2). */
	roleSocioLabels?: Record<string, string>;
	/** Copy del hero en la pantalla de conexión de cuenta. */
	benefit: string;
	/** Hero copy por ROL cuando la promesa cambia con la elección del usuario
	 * (DiDi: activación vs. declaraciones). resolveAllyBenefit lo resuelve con
	 * el rol de answers y cae a `benefit` sin rol (la pregunta de intención se
	 * pinta con `benefit`, que por eso debe ser neutro entre las opciones). */
	roleBenefits?: Record<string, string>;
	/** Copy del hero en la pantalla del plan. */
	planLabel: string;
	planId: MonthlyPlanId;
	/** Plan por ROL cuando el convenio del aliado cubre varias familias (AG
	 * Start: plataformas/RESICO/empresarial). La pantalla del plan resuelve
	 * rolePlanIds[rol] con resolveAllyPlanId y cae a planId sin rol. */
	rolePlanIds?: Partial<Record<string, MonthlyPlanId>>;
	cardTitle?: string;
	cardSubtitle?: string;
	cardDescription: string;
	/** Overrides de la lista de beneficios del plan (framing Zubale). */
	cardBenefits?: string[];
	/** Solo alcanzable por su URL de campaña (`?partner=`): NO es una choice del
	 * nodo `ally_grid`. Es data declarada y no derivada del flow porque el ctx
	 * del host la necesita sin cargar el JSON; la paridad catálogo ⇄ grid la
	 * exige admatticCampaign.test.ts.
	 *
	 * Marca al aliado como tráfico PAGADO: su llegada vale una comisión, así que
	 * su link entra directo a la alianza (hasDirectCampaignLanding). */
	campaignOnly?: boolean;
	/** Aliado del GRID cuyo link de campaña entra directo a su alianza, igual
	 * que uno de link exclusivo: sin la intro genérica, sin «atrás» al grid y con
	 * su organización ligada al entrar. Solo aplica a quien llegó por ESE link;
	 * quien lo elige en el grid hace el recorrido normal. Los de `campaignOnly`
	 * ya lo traen implícito. */
	directCampaignLanding?: boolean;
	/** El anuncio del aliado YA SABE el segmento del usuario y lo manda en la
	 * URL (`?calc=` de Calcu.mx: la calculadora que el visitante acaba de usar
	 * dice su régimen). El deep-link traduce el slug con `choiceBySlug` y
	 * pre-estampa `answerKey`, así que la pregunta de rol del aliado llega
	 * contestada y el walk la pasa de largo.
	 *
	 * Es DATA y no un caso especial del deep-link porque el mapa slug → choice
	 * es del CONVENIO: lo pacta cada aliado con su propia taxonomía, y el motor
	 * de campañas no tiene por qué conocer ninguna. Un slug fuera del mapa se
	 * ignora y el usuario contesta la pregunta, que es la degradación correcta.
	 *
	 * La llave donde se estampa NO se declara aquí: es `roleAnswerKey`, la misma
	 * que lee `resolveAllyRoleAnswer`. Nombrarla dos veces la dejaría divergir, y
	 * un prestamp sobre una llave que ningún nodo guarda reaparece como pregunta.
	 * Las choices SÍ tienen que existir en ese nodo. */
	campaignRole?: {
		label: string;
		choiceBySlug: Record<string, string>;
	};
	/** Video tutorial del aliado (mp4 del bundle, vertical). Después de elegir el
	 * rol, la pantalla del correo lo destaca como tarjeta y el resto del branch
	 * lo ofrece como botón chico en el hero, por si la persona quiere volver a
	 * verlo. `roles`: solo con esa respuesta de rol se ofrece (el de DiDi
	 * explica la asistencia fiscal, no el plan). Ausente = sin video. */
	tutorialVideo?: {
		src: string;
		title: string;
		subtitle: string;
		avatar: string;
		poster: string;
		roles?: string[];
	};
	/** Días que el link del aliado sigue acreditando el alta ANTES del registro
	 * (el target de campaña guardado en el navegador), contados desde el PRIMER
	 * clic: volver a tocar el link del mismo aliado no los renueva, y la fecha
	 * de ese primer clic viaja en el prestamp (`partner_first_click_at`). Es del
	 * convenio, no del motor de campañas. Ausente = 7 días desde el ÚLTIMO clic. */
	attributionWindowDays?: number;
	/** El aliado tiene un segundo link que vende la declaración anual en vez del
	 * plan: el mismo `?partner=` más `?offer=annual`. No agrega nada al carrito:
	 * se estampa `campaign_offer: 'annual'` y la pantalla del plan muestra la
	 * tarjeta co-brandeada de la anual en lugar del plan. El descuento es el cupón
	 * del tier restringido al product version de la anual. `heroLine` reemplaza a
	 * la del rol, que habla del plan mensual. */
	annualOffer?: { heroLine: string };
	/** El precio del convenio que la landing del aliado (el login co-brandeado)
	 * promete ANTES del registro, por familia de plan, en centavos mensuales.
	 *
	 * Quemado a propósito: quien ve esa pantalla todavía no tiene sesión, y
	 * `/pricing/catalog` y `/organizations` responden 401 sin token, así que no
	 * hay de dónde leer el precio de Talon. La fuente de verdad sigue siendo el
	 * cupón del tier del aliado en sales-transaction-engine: si el convenio
	 * cambia allá, se cambia aquí. Después del registro, la pantalla del plan y
	 * el checkout ya leen el precio real.
	 *
	 * Ausente = el login no habla de precio. */
	landingPrices?: Record<MonthlyPlanId, { listCents: number; allyCents: number }>;
	/** Organización backend del aliado (catálogo /organizations/web/all) — la
	 * consume el host al ejecutar el effect 'ally-org-link' (vincula al usuario
	 * con la org usando el correo validado como org_code, y como en v2 también
	 * actualiza el email del perfil). String plano: el enum ORGANIZATIONS no es
	 * importable desde una pantalla pura; la paridad de valores la exige
	 * allyCatalog.test.ts. */
	orgName?: string;
	/** Campaña de trial activa del aliado (ver AllyTrial). */
	trial?: AllyTrial;
	/** Content packs de la capa de capacidades, llaveados por rol ('default' para
	 * aliados de una sola actividad, 'agente'/'propietario' para Morada Uno).
	 * REQUERIDO: la capa de capacidades es visible para TODO aliado que llega a
	 * ally_capabilities, así que el compilador exige que cada aliado traiga su
	 * pack — un aliado sin content no puede pintar la pantalla. Lo resuelve
	 * resolveAllyRoleContent. */
	content: Record<string, AllyRoleContent>;
	/** Capacidad de cumplimiento fiscal del convenio (ver AllyCompliance). */
	compliance?: AllyCompliance;
};

/** Content pack del Programa de Activación Fiscal DiDi (rol 'activation' y
 * default de DiDi). Copy espejo del de Zubale con el feedback de DiDi del
 * 2026-09-10 aplicado (t-056, t-168). */
const DIDI_ACTIVATION_CONTENT: AllyRoleContent = {
	label: 'socio repartidor',
	regime: 'Régimen de plataformas tecnológicas (625)',
	heroLine:
		'Gracias a la alianza oficial con DiDi, heru pone en orden tu perfil fiscal para que puedas recibir más tipos de pedidos en DiDi y sigas repartiendo sin trabas con el SAT.',
	modules: [
		{
			icon: 'CheckCircle',
			title: 'Tu activación fiscal con DiDi, completa',
			copy: 'Verificamos y dejamos en orden los requisitos que DiDi pide para que recibas más tipos de pedidos: RFC activo, régimen de plataformas, actividad de reparto, e.firma y sellos vigentes.',
			featured: true,
		},
		{
			icon: 'Files',
			title: 'e.firma, sellos y alta: te ayudamos con todo',
			copy: 'Te acompañamos a tramitar tu e.firma, tus sellos de facturación (CSD) y tu alta ante el SAT. Todo lo que necesitas para cobrar como socio repartidor.',
		},
		{
			icon: 'Receipt',
			title: 'Presentamos tu declaración mensual',
			copy: 'Con tu plan activo, calculamos y declaramos ante el SAT los impuestos de tus entregas, cada mes.',
		},
		{
			icon: 'ChartLineUp',
			title: 'Detectamos tus deducciones',
			copy: 'Gasolina, celular, equipo de reparto… aplicamos lo deducible para que pagues menos.',
		},
		{
			icon: 'Headset',
			title: 'Un contador cuando lo necesites',
			copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
		},
	],
};

export const ALLY_CATALOG: Record<AllyId, Ally> = {
	/**
	 * Admattic — red de AFILIADOS, no una plataforma de trabajo.
	 *
	 * El usuario NO trabaja con Admattic: llegó por un anuncio suyo en WhatsApp
	 * y probablemente ni sabe quién son. Por eso el copy dice "llegas con" y
	 * nunca "eres socio de" / "trabajas con" — no le atribuimos una relación que
	 * no tiene. El co-branding existe para que el anuncio y la pantalla se vean
	 * como la misma cosa, que es lo que sostiene la conversión.
	 *
	 * `orgName` es lo que HACE el descuento de la alianza, no un adorno: el effect
	 * 'ally-org-link' resuelve la organización por ese nombre y vincula al usuario
	 * con su tier. De ahí cuelgan los DOS motores de descuento — el cupón del tier
	 * (suscripciones legacy) y el atributo de perfil `Organization` de Talon One
	 * (carrito). Sin él el usuario ve precio de lista, que es justo lo contrario
	 * de lo que promete la propuesta ("precio preferente aplicado en automático").
	 * Exige que la organización exista en el backend con su default_tier_id.
	 *
	 * Sin `trial`: el mes gratis vigente es global desde el 2026-07-22, no una
	 * campaña registrada en plans service — inventarle un `campaign` aquí
	 * mandaría una cadena que el backend no conoce.
	 *
	 * NO aparece en las choices del nodo `ally_grid` del flow: solo se llega por
	 * `?partner=admattic`. Es la única entrada del catálogo con esa propiedad, y
	 * es deliberada — ver designs/alianza-admattic-afiliados.md.
	 */
	admattic: {
		id: 'admattic',
		name: 'Admattic',
		logo: 'admattic',
		bg: '#E50B3D',
		campaignOnly: true,
		roleAnswerKey: ADMATTIC_ROLE_ANSWER_KEY,
		socioLabel: 'contribuyente',
		benefit:
			'Llegas con Admattic. heru pone tus impuestos en orden —declaraciones, facturas y SAT— sin que tengas que entenderle a nada.',
		planLabel: 'Plan con beneficio Admattic',
		planId: 'plataformas',
		// La campaña es masiva por WhatsApp y no sabemos a qué segmento le cae,
		// así que el rol (admattic_role) decide el plan en vez de adivinarlo.
		rolePlanIds: {
			plataformas: 'plataformas',
			independiente: 'profesionista',
			empresarial: 'empresarial',
		},
		// El default de AllyPlanScreen arma `Para ${socioLabel}es de {ally}`, regla
		// que solo funciona con labels terminados en consonante (repartidor →
		// repartidores). 'contribuyente' salía "contribuyentees", así que el
		// subtítulo va explícito, como en Zubale y Morada Uno.
		cardSubtitle: 'Con el beneficio de tu alianza',
		cardDescription:
			'heru declara tus impuestos, factura por ti y te respalda con contadores expertos.',
		// Debe coincidir EXACTO con organization.name del backend: es la llave de
		// match de resolveOrgByName y la mitad del atributo de Talon
		// ("<org> <tier>"). Un typo aquí = usuario sin descuento, en silencio.
		orgName: 'Admattic',
		content: {
			plataformas: {
				label: 'socio de plataformas',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'Llegas con Admattic. heru declara los impuestos de tus plataformas cada mes para que tú sigas generando.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus plataformas, cada mes antes del día 17.',
					},
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos con tu e.firma, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, internet… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			independiente: {
				label: 'profesionista independiente',
				regime: 'RESICO — Régimen Simplificado de Confianza',
				heroLine:
					'Llegas con Admattic. heru presenta tus declaraciones y factura por ti, para que te dediques a lo tuyo.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tu declaración RESICO cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones por honorarios y servicios.',
					},
					{
						icon: 'Files',
						title: 'Factura a tus clientes en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'CheckCircle',
						title: 'Alta y régimen sin vueltas al SAT',
						copy: 'Te ayudamos con tu alta en RESICO, e.firma y constancia para facturar desde ya.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Equipo, internet, formación… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			empresarial: {
				label: 'dueño de negocio',
				regime: 'Actividad Empresarial y Profesional',
				heroLine:
					'Llegas con Admattic. heru lleva la contabilidad fiscal de tu negocio: declaraciones, facturas y deducciones al día.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Declaraciones de tu actividad cada mes',
						copy: 'Un contador calcula tus utilidades y presenta tus declaraciones de Actividad Empresarial o RIF.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Conciliamos ingresos y gastos',
						copy: 'Cruzamos tus facturas del SAT con tus gastos deducibles para optimizar tu carga fiscal.',
					},
					{
						icon: 'Files',
						title: 'Facturación incluida',
						copy: 'Emite CFDI a tus clientes y administra tus comprobantes desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Asesoría de expertos',
						copy: 'Especialistas fiscales revisan tu caso y resuelven tus dudas por chat.',
					},
				],
			},
			// Red de seguridad: un record de la 2.5 (CONGELADA, sin nodo de rol)
			// que llegue por `?partner=admattic` cae aquí y ve algo coherente.
			default: {
				label: 'contribuyente',
				regime: 'El régimen que te corresponda',
				heroLine:
					'Llegas con Admattic. heru presenta tus declaraciones, factura por ti y te respalda con contadores expertos.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tus declaraciones presentadas cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones ante el SAT.',
					},
					{
						icon: 'Files',
						title: 'Factura en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Aplicamos todo lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	/**
	 * Digital Hub — la red de afiliados dueña del tracker (`digitalhub.g2afse.com`).
	 *
	 * Mismo molde que Admattic y por las mismas razones (ver el comentario de
	 * arriba): el usuario NO trabaja con Digital Hub, así que el copy dice "llegas
	 * con" y nunca "eres socio de"; no aparece en el grid; el rol decide el plan.
	 *
	 * Lo que cambia es el trato, no la configuración: su comisión es 20% SIN piso
	 * de volumen (Admattic era 25% con piso de 50 conversiones/mes). La comisión
	 * es lo que les pagamos, no lo que descuenta el usuario — los tres precios al
	 * público son idénticos.
	 */
	digitalhub: {
		id: 'digitalhub',
		name: 'Digital Hub',
		logo: 'digitalhub',
		bg: '#1BA9E8',
		campaignOnly: true,
		roleAnswerKey: DIGITALHUB_ROLE_ANSWER_KEY,
		socioLabel: 'contribuyente',
		benefit:
			'Llegas con Digital Hub. heru pone tus impuestos en orden —declaraciones, facturas y SAT— sin que tengas que entenderle a nada.',
		planLabel: 'Plan con beneficio Digital Hub',
		planId: 'plataformas',
		// La campaña es masiva por WhatsApp y no sabemos a qué segmento le cae,
		// así que el rol (digitalhub_role) decide el plan en vez de adivinarlo.
		rolePlanIds: {
			plataformas: 'plataformas',
			independiente: 'profesionista',
			empresarial: 'empresarial',
		},
		// El default de AllyPlanScreen arma `Para ${socioLabel}es de {ally}`, regla
		// que solo funciona con labels terminados en consonante (repartidor →
		// repartidores). 'contribuyente' salía "contribuyentees", así que el
		// subtítulo va explícito, como en Zubale y Morada Uno.
		cardSubtitle: 'Con el beneficio de tu alianza',
		cardDescription:
			'heru declara tus impuestos, factura por ti y te respalda con contadores expertos.',
		// Debe coincidir EXACTO con organization.name del backend: es la llave de
		// match de resolveOrgByName y la mitad del atributo de Talon
		// ("<org> <tier>"). Un typo aquí = usuario sin descuento, en silencio.
		orgName: 'Digital Hub',
		content: {
			plataformas: {
				label: 'socio de plataformas',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'Llegas con Digital Hub. heru declara los impuestos de tus plataformas cada mes para que tú sigas generando.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus plataformas, cada mes antes del día 17.',
					},
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos con tu e.firma, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, internet… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			independiente: {
				label: 'profesionista independiente',
				regime: 'RESICO — Régimen Simplificado de Confianza',
				heroLine:
					'Llegas con Digital Hub. heru presenta tus declaraciones y factura por ti, para que te dediques a lo tuyo.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tu declaración RESICO cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones por honorarios y servicios.',
					},
					{
						icon: 'Files',
						title: 'Factura a tus clientes en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'CheckCircle',
						title: 'Alta y régimen sin vueltas al SAT',
						copy: 'Te ayudamos con tu alta en RESICO, e.firma y constancia para facturar desde ya.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Equipo, internet, formación… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			empresarial: {
				label: 'dueño de negocio',
				regime: 'Actividad Empresarial y Profesional',
				heroLine:
					'Llegas con Digital Hub. heru lleva la contabilidad fiscal de tu negocio: declaraciones, facturas y deducciones al día.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Declaraciones de tu actividad cada mes',
						copy: 'Un contador calcula tus utilidades y presenta tus declaraciones de Actividad Empresarial o RIF.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Conciliamos ingresos y gastos',
						copy: 'Cruzamos tus facturas del SAT con tus gastos deducibles para optimizar tu carga fiscal.',
					},
					{
						icon: 'Files',
						title: 'Facturación incluida',
						copy: 'Emite CFDI a tus clientes y administra tus comprobantes desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Asesoría de expertos',
						copy: 'Especialistas fiscales revisan tu caso y resuelven tus dudas por chat.',
					},
				],
			},
			// Red de seguridad: un record de la 2.5 (CONGELADA, sin nodo de rol)
			// que llegue por `?partner=admattic` cae aquí y ve algo coherente.
			default: {
				label: 'contribuyente',
				regime: 'El régimen que te corresponda',
				heroLine:
					'Llegas con Digital Hub. heru presenta tus declaraciones, factura por ti y te respalda con contadores expertos.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tus declaraciones presentadas cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones ante el SAT.',
					},
					{
						icon: 'Files',
						title: 'Factura en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Aplicamos todo lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	/**
	 * Maslow — app de beneficios para empleados de empresas.
	 *
	 * El usuario NO trabaja con Maslow, así que el copy dice "llegas con". Está en
	 * el grid, como Calcu.mx, y su link de campaña entra directo a la alianza
	 * (`directCampaignLanding`). El link es UNO solo (sin `campaignRole`), por lo
	 * que el usuario contesta la pregunta de rol y el rol decide el plan. El
	 * descuento (25%) vive en el tier de la organización "Maslow" del backend, no
	 * en un cupón del front. El mismo tier lleva 40% en la declaración anual
	 * (product_version 130).
	 */
	maslow: {
		id: 'maslow',
		name: 'Maslow',
		logo: 'maslow',
		bg: '#7345D2',
		directCampaignLanding: true,
		// Convenio: 25% sobre el precio de lista para las tres familias de plan.
		// Es el precio que cobra el cupón del tier Maslow (sales-transaction-engine,
		// forever); Talon redondea al peso. Lo promete el login co-brandeado.
		landingPrices: {
			plataformas: { listCents: 24900, allyCents: 18700 },
			profesionista: { listCents: 79900, allyCents: 59900 },
			empresarial: { listCents: 199900, allyCents: 149900 },
		},
		// Link B (`&offer=annual`): la declaración anual con 40%, por el cupón
		// MaslowPartnerAnual del mismo tier. Aterriza en la tarjeta de la anual.
		annualOffer: {
			heroLine:
				'Llegas con Maslow. Un contador de heru prepara y presenta tu declaración anual, y gestiona tu devolución si tienes saldo a favor.',
		},
		roleAnswerKey: MASLOW_ROLE_ANSWER_KEY,
		socioLabel: 'contribuyente',
		benefit:
			'Llegas con Maslow. heru pone tus impuestos en orden: declaraciones, facturas y SAT, sin que tengas que entenderle a nada.',
		planLabel: 'Plan con beneficio Maslow',
		planId: 'plataformas',
		// El link es uno solo para los tres regímenes y no sabemos a qué segmento le cae,
		// así que el rol (maslow_role) decide el plan en vez de adivinarlo.
		rolePlanIds: {
			plataformas: 'plataformas',
			independiente: 'profesionista',
			empresarial: 'empresarial',
		},
		// El default de AllyPlanScreen arma `Para ${socioLabel}es de {ally}`, regla
		// que solo funciona con labels terminados en consonante (repartidor →
		// repartidores). 'contribuyente' salía "contribuyentees", así que el
		// subtítulo va explícito, como en Zubale y Morada Uno.
		cardSubtitle: 'Con el beneficio de tu alianza',
		cardDescription:
			'heru declara tus impuestos, factura por ti y te respalda con contadores expertos.',
		// Debe coincidir EXACTO con organization.name del backend: es la llave de
		// match de resolveOrgByName y la mitad del atributo de Talon
		// ("<org> <tier>"). Un typo aquí = usuario sin descuento, en silencio.
		orgName: 'Maslow',
		content: {
			plataformas: {
				label: 'socio de plataformas',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'Llegas con Maslow. heru declara los impuestos de tus plataformas cada mes para que tú sigas generando.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus plataformas, cada mes antes del día 17.',
					},
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos con tu e.firma, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, internet… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			independiente: {
				label: 'profesionista independiente',
				regime: 'RESICO: Régimen Simplificado de Confianza',
				heroLine:
					'Llegas con Maslow. heru presenta tus declaraciones y factura por ti, para que te dediques a lo tuyo.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tu declaración RESICO cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones por honorarios y servicios.',
					},
					{
						icon: 'Files',
						title: 'Factura a tus clientes en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'CheckCircle',
						title: 'Alta y régimen sin vueltas al SAT',
						copy: 'Te ayudamos con tu alta en RESICO, e.firma y constancia para facturar desde ya.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Equipo, internet, formación… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			empresarial: {
				label: 'dueño de negocio',
				regime: 'Actividad Empresarial y Profesional',
				heroLine:
					'Llegas con Maslow. heru lleva la contabilidad fiscal de tu negocio: declaraciones, facturas y deducciones al día.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Declaraciones de tu actividad cada mes',
						copy: 'Un contador calcula tus utilidades y presenta tus declaraciones de Actividad Empresarial o RIF.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Conciliamos ingresos y gastos',
						copy: 'Cruzamos tus facturas del SAT con tus gastos deducibles para optimizar tu carga fiscal.',
					},
					{
						icon: 'Files',
						title: 'Facturación incluida',
						copy: 'Emite CFDI a tus clientes y administra tus comprobantes desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Asesoría de expertos',
						copy: 'Especialistas fiscales revisan tu caso y resuelven tus dudas por chat.',
					},
				],
			},
			// Red de seguridad: un record de la 2.5 (CONGELADA, sin nodo de rol)
			// que llegue por `?partner=maslow` cae aquí y ve algo coherente.
			default: {
				label: 'contribuyente',
				regime: 'El régimen que te corresponda',
				heroLine:
					'Llegas con Maslow. heru presenta tus declaraciones, factura por ti y te respalda con contadores expertos.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tus declaraciones presentadas cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones ante el SAT.',
					},
					{
						icon: 'Files',
						title: 'Factura en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Aplicamos todo lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	/**
	 * Calcu.mx — la calculadora de impuestos que muestra el anuncio de heru en
	 * cuanto alguien termina un cálculo.
	 *
	 * Mismo molde de copy que Admattic y Digital Hub (red de adquisición, no
	 * plataforma de trabajo): dice "llegas con" y nunca "eres socio de", y el rol
	 * decide el plan. A diferencia de ellos vive en el grid; su link conserva la
	 * entrada directa por `directCampaignLanding`.
	 *
	 * Lo que la distingue: el usuario NO llega en frío. Acaba de calcular su ISR
	 * de RESICO, de plataformas o de actividad empresarial, así que su régimen ya
	 * es un hecho conocido y viaja en `?calc=`. Por eso es el primer aliado con
	 * `campaignRole`: los tres anuncios entran a tres brazos distintos y nadie
	 * vuelve a contestar lo que la calculadora ya sabía.
	 */
	calcumx: {
		id: 'calcumx',
		name: 'Calcu.mx',
		logo: 'calcumx',
		bg: '#2563EB',
		// En el grid como cualquier aliado, pero quien llega por su link viene
		// de terminar un cálculo: entra directo a la alianza, sin intro genérica.
		directCampaignLanding: true,
		socioLabel: 'contribuyente',
		benefit:
			'Llegas con Calcu.mx. Ya calculaste lo que debes; heru se encarga de presentarlo ante el SAT cada mes.',
		// El anuncio ya habló del régimen del usuario, así que el hero del login
		// y de la capa de capacidades sigue esa misma conversación en vez de
		// volver a la promesa genérica.
		roleBenefits: {
			plataformas:
				'Llegas con Calcu.mx. Ya viste lo que te retienen las apps; heru declara ante el SAT el ISR y el IVA de tus plataformas, cada mes.',
			independiente:
				'Llegas con Calcu.mx. Ya sabes cuánto debes; contadores de heru presentan tu RESICO cada mes y revisan tus deducciones.',
			empresarial:
				'Llegas con Calcu.mx. Facturas, deduces y declaras cada mes; heru lo carga por ti de principio a fin.',
		},
		planLabel: 'Plan con beneficio Calcu.mx',
		planId: 'plataformas',
		roleAnswerKey: CALCUMX_ROLE_ANSWER_KEY,
		// Las tres calculadoras del convenio caen en las tres familias de plan.
		rolePlanIds: {
			plataformas: 'plataformas',
			independiente: 'profesionista',
			empresarial: 'empresarial',
		},
		// El slug del anuncio es el de la CALCULADORA que el usuario acababa de
		// usar, no el de nuestra taxonomía de planes: así Calcu.mx pega el mismo
		// nombre que ya tiene en sus páginas de resultado y no tiene que aprender
		// el nuestro.
		campaignRole: {
			label: '¿Cómo generas tus ingresos?',
			choiceBySlug: {
				platforms: 'plataformas',
				resico: 'independiente',
				business: 'empresarial',
			},
		},
		// Cláusula 4ª del convenio: cuenta el alta hecha dentro de los 30 días
		// naturales siguientes al primer clic.
		attributionWindowDays: 30,
		// Los cupones del tier Calcu.mx (sales-transaction-engine, forever) sobre
		// el precio de lista: CalcuMxPartner 25% (pv 139), CalcuMxPartnerResico
		// 37.5% (pv 141) y CalcuMxPartnerEmpresarial 55% (pv 143). Talon redondea
		// al peso, así que se estampa el precio que cobra, no el porcentaje.
		landingPrices: {
			plataformas: { listCents: 24900, allyCents: 18700 },
			profesionista: { listCents: 79900, allyCents: 49900 },
			empresarial: { listCents: 199900, allyCents: 90000 },
		},
		// El default de AllyPlanScreen arma `Para ${socioLabel}es de {ally}` y
		// 'contribuyente' salía "contribuyentees", así que el subtítulo va
		// explícito, como en Admattic y Digital Hub.
		cardSubtitle: 'Con el beneficio de tu alianza',
		cardDescription:
			'heru declara tus impuestos, factura por ti y te respalda con contadores expertos.',
		// Debe coincidir EXACTO con organization.name del backend: es la llave de
		// match de resolveOrgByName y la mitad del atributo de Talon
		// ("<org> <tier>"). Un typo aquí = usuario sin descuento, en silencio.
		orgName: 'Calcu.mx',
		content: {
			plataformas: {
				label: 'socio de plataformas',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'Ya viste lo que te retienen las apps. heru declara ese ISR y ese IVA ante el SAT cada mes, por ti.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tu declaración mensual, presentada',
						copy: 'Calculamos y presentamos ante el SAT el ISR y el IVA de Uber, DiDi, Rappi, Airbnb y las demás plataformas, antes del día 17.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, mantenimiento: aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'CheckCircle',
						title: 'Alta, e.firma y sellos: te acompañamos',
						copy: 'Te ayudamos con tu alta ante el SAT, tu e.firma y tus sellos de facturación.',
					},
					{
						icon: 'Files',
						title: 'Factura cuando la necesites',
						copy: 'Emite CFDI y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			independiente: {
				label: 'profesionista en RESICO',
				regime: 'RESICO, Régimen Simplificado de Confianza',
				heroLine:
					'Ya sabes cuánto debes. heru lo presenta por ti: contadores expertos declaran tu RESICO cada mes.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tu declaración RESICO cada mes',
						copy: 'Contadores expertos calculan y presentan tu ISR y tu IVA por honorarios y servicios.',
					},
					{
						icon: 'Files',
						title: 'Factura a tus clientes en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'CheckCircle',
						title: 'Alta en RESICO sin vueltas al SAT',
						copy: 'Te ayudamos con tu alta, tu e.firma y tu constancia para que factures desde ya.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Revisamos tus deducciones',
						copy: 'Equipo, internet, formación: aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			empresarial: {
				label: 'contribuyente con actividad empresarial',
				regime: 'Actividad Empresarial y Profesional (612) o RIF',
				heroLine:
					'Facturas, deduces y declaras cada mes. heru lo carga por ti, de principio a fin.',
				modules: [
					{
						icon: 'Receipt',
						title: 'ISR e IVA declarados cada mes',
						copy: 'Un contador calcula tus utilidades, concilia tus gastos y presenta tus declaraciones ante el SAT.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Tus gastos deducibles, conciliados',
						copy: 'Cruzamos tus facturas del SAT con tus gastos para optimizar tu carga fiscal.',
					},
					{
						icon: 'Files',
						title: 'Facturación incluida',
						copy: 'Emite CFDI a tus clientes y administra tus comprobantes desde la app.',
					},
					{
						icon: 'CheckCircle',
						title: 'Los trámites del SAT, resueltos',
						copy: 'Alta, e.firma, sellos y avisos: los hacemos contigo.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			// Red de seguridad: el link pelado (sin `?calc=`) sobre un record de la
			// 2.5, que no tiene nodo de rol, cae aquí y ve algo coherente.
			default: {
				label: 'contribuyente',
				regime: 'El régimen que te corresponda',
				heroLine:
					'Llegas con Calcu.mx. heru presenta tus declaraciones, factura por ti y te respalda con contadores expertos.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tus declaraciones presentadas cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones ante el SAT.',
					},
					{
						icon: 'Files',
						title: 'Factura en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Aplicamos todo lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	/**
	 * TUAL — app para médicos. Misma configuración que Calcu.mx (entrada directa
	 * por link, ventana de atribución de 30 días, mismos precios y descuentos);
	 * cambia el copy, dirigido a doctores, y el plan por omisión: a diferencia de
	 * Calcu.mx (`plataformas`), el de TUAL es RESICO (`profesionista`). Sin
	 * `campaignRole`: su link no trae `?calc=`, así que el rol se contesta en el
	 * flow; el médico aún puede elegir otro plan y RESICO solo es el valor por
	 * omisión.
	 */
	tual: {
		id: 'tual',
		name: 'TUAL',
		logo: 'tual',
		bg: '#2563EB',
		directCampaignLanding: true,
		socioLabel: 'doctor',
		benefit:
			'Llegas con TUAL. Atiendes pacientes; heru factura por ti y presenta tus impuestos ante el SAT cada mes.',
		roleBenefits: {
			plataformas:
				'Llegas con TUAL. Además de tu consulta, trabajas con plataformas; heru declara ante el SAT el ISR y el IVA de tus plataformas, cada mes.',
			independiente:
				'Llegas con TUAL. Atiendes pacientes; contadores de heru presentan tu RESICO cada mes y revisan tus deducciones.',
			empresarial:
				'Llegas con TUAL. Tienes un consultorio o clínica; heru factura, deduce y declara cada mes por ti, de principio a fin.',
		},
		planLabel: 'Plan con beneficio TUAL',
		planId: 'profesionista',
		roleAnswerKey: TUAL_ROLE_ANSWER_KEY,
		rolePlanIds: {
			plataformas: 'plataformas',
			independiente: 'profesionista',
			empresarial: 'empresarial',
		},
		attributionWindowDays: 30,
		// Cupones del tier TUAL: los mismos que Calcu.mx.
		landingPrices: {
			plataformas: { listCents: 24900, allyCents: 18700 },
			profesionista: { listCents: 79900, allyCents: 49900 },
			empresarial: { listCents: 199900, allyCents: 90000 },
		},
		cardSubtitle: 'Con el beneficio de tu alianza',
		cardDescription:
			'heru declara tus impuestos, factura por ti y te respalda con contadores expertos.',
		orgName: 'TUAL',
		content: {
			plataformas: {
				label: 'socio de plataformas',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'Además de tu consulta, trabajas con plataformas. heru declara ese ISR y ese IVA ante el SAT cada mes, por ti.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tu declaración mensual, presentada',
						copy: 'Calculamos y presentamos ante el SAT el ISR y el IVA de Uber, DiDi, Rappi, Airbnb y las demás plataformas, antes del día 17.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, mantenimiento: aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'CheckCircle',
						title: 'Alta, e.firma y sellos: te acompañamos',
						copy: 'Te ayudamos con tu alta ante el SAT, tu e.firma y tus sellos de facturación.',
					},
					{
						icon: 'Files',
						title: 'Factura cuando la necesites',
						copy: 'Emite CFDI y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			independiente: {
				label: 'doctor en RESICO',
				regime: 'RESICO, Régimen Simplificado de Confianza',
				heroLine:
					'Atiendes pacientes, heru atiende tus impuestos. Contadores expertos declaran tu RESICO cada mes.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tu declaración RESICO cada mes',
						copy: 'Contadores expertos calculan y presentan tu ISR y tu IVA por tus honorarios médicos.',
					},
					{
						icon: 'Files',
						title: 'Factura a tus pacientes en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'CheckCircle',
						title: 'Alta en RESICO sin vueltas al SAT',
						copy: 'Te ayudamos con tu alta, tu e.firma y tu constancia para que factures desde ya.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Revisamos tus deducciones',
						copy: 'Equipo médico, consultorio, insumos: aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			empresarial: {
				label: 'contribuyente con actividad empresarial',
				regime: 'Actividad Empresarial y Profesional (612) o RIF',
				heroLine:
					'Tu consultorio factura, deduce y declara cada mes. heru lo carga por ti, de principio a fin.',
				modules: [
					{
						icon: 'Receipt',
						title: 'ISR e IVA declarados cada mes',
						copy: 'Un contador calcula tus utilidades, concilia tus gastos y presenta tus declaraciones ante el SAT.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Tus gastos deducibles, conciliados',
						copy: 'Cruzamos tus facturas del SAT con tus gastos para optimizar tu carga fiscal.',
					},
					{
						icon: 'Files',
						title: 'Facturación incluida',
						copy: 'Emite CFDI a tus clientes y administra tus comprobantes desde la app.',
					},
					{
						icon: 'CheckCircle',
						title: 'Los trámites del SAT, resueltos',
						copy: 'Alta, e.firma, sellos y avisos: los hacemos contigo.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			default: {
				label: 'contribuyente',
				regime: 'El régimen que te corresponda',
				heroLine:
					'Llegas con TUAL. heru presenta tus declaraciones, factura por tus consultas y te respalda con contadores expertos.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tus declaraciones presentadas cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones ante el SAT.',
					},
					{
						icon: 'Files',
						title: 'Factura en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Aplicamos todo lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	plata: {
		id: 'plata',
		name: 'PLATA',
		logo: 'plata',
		bg: '#FF5D15',
		directCampaignLanding: true,
		socioLabel: 'cliente',
		benefit:
			'Llegas con PLATA. heru presenta tus declaraciones ante el SAT cada mes, factura por ti y te respalda con contadores expertos.',
		roleBenefits: {
			plataformas:
				'Llegas con PLATA. heru declara ante el SAT el ISR y el IVA de tus plataformas, cada mes.',
			resico:
				'Llegas con PLATA. Contadores de heru presentan tu RESICO cada mes y revisan tus deducciones.',
			business:
				'Llegas con PLATA. heru factura, deduce y declara cada mes por ti, de principio a fin.',
		},
		planLabel: 'Plan para clientes PLATA',
		planId: 'profesionista',
		roleAnswerKey: PLATA_ROLE_ANSWER_KEY,
		rolePlanIds: {
			plataformas: 'plataformas',
			resico: 'profesionista',
			business: 'empresarial',
		},
		attributionWindowDays: 30,
		cardSubtitle: 'Con tu alianza PLATA',
		cardDescription:
			'heru declara tus impuestos, factura por ti y te respalda con contadores expertos.',
		orgName: 'PLATA',
		content: {
			plataformas: {
				label: 'socio de plataformas',
				regime: 'Régimen de Plataformas Tecnológicas (625)',
				heroLine:
					'Llegas con PLATA. heru declara cada mes el ISR y el IVA de tus plataformas, por ti.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tu declaración mensual, presentada',
						copy: 'Calculamos y presentamos ante el SAT el ISR y el IVA de Uber, DiDi, Rappi, Airbnb y las demás plataformas, antes del día 17.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, mantenimiento: aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'CheckCircle',
						title: 'Alta, e.firma y sellos: te acompañamos',
						copy: 'Te ayudamos con tu alta ante el SAT, tu e.firma y tus sellos de facturación.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			resico: {
				label: 'cliente en RESICO',
				regime: 'RESICO, Régimen Simplificado de Confianza',
				heroLine:
					'Llegas con PLATA. Contadores expertos de heru declaran tu RESICO cada mes, por ti.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tu declaración RESICO cada mes',
						copy: 'Contadores expertos calculan y presentan tu ISR y tu IVA ante el SAT.',
					},
					{
						icon: 'Files',
						title: 'Factura en segundos',
						copy: 'CFDI ilimitados desde la app, con timbrado al instante.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Revisamos tus deducciones',
						copy: 'Aplicamos todo lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			business: {
				label: 'contribuyente con actividad empresarial',
				regime: 'Actividad Empresarial y Profesional (612)',
				heroLine:
					'Llegas con PLATA. heru factura, deduce y declara cada mes por ti, de principio a fin.',
				modules: [
					{
						icon: 'Receipt',
						title: 'ISR e IVA declarados cada mes',
						copy: 'Un contador calcula tus utilidades, concilia tus gastos y presenta tus declaraciones ante el SAT.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Tus gastos deducibles, conciliados',
						copy: 'Cruzamos tus facturas del SAT con tus gastos para optimizar tu carga fiscal.',
					},
					{
						icon: 'Files',
						title: 'Facturación ilimitada',
						copy: 'Emite CFDI a tus clientes y administra tus comprobantes desde la app.',
					},
					{
						icon: 'CheckCircle',
						title: 'Los trámites del SAT, resueltos',
						copy: 'Alta, e.firma, sellos y avisos: los hacemos contigo.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			default: {
				label: 'contribuyente',
				regime: 'El régimen que te corresponda',
				heroLine:
					'Llegas con PLATA. heru presenta tus declaraciones, factura por ti y te respalda con contadores expertos.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tus declaraciones presentadas cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones ante el SAT.',
					},
					{
						icon: 'Files',
						title: 'Factura en segundos',
						copy: 'CFDI ilimitados desde la app, con timbrado al instante.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Aplicamos todo lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	ag_enterprises: {
		id: 'ag_enterprises',
		name: 'AG Enterprises',
		logo: 'ag',
		bg: '#000000',
		roleAnswerKey: AG_ROLE_ANSWER_KEY,
		socioLabel: 'emprendedor',
		benefit:
			'Eres parte de la comunidad AG Start. heru pone tus impuestos en orden —declaraciones, facturas y SAT— para que te dediques a hacer crecer tu proyecto.',
		planLabel: 'Plan para emprendedores de AG Start',
		planId: 'profesionista',
		// El convenio cubre las TRES familias: el rol de ag_role decide el plan.
		rolePlanIds: {
			plataformas: 'plataformas',
			independiente: 'profesionista',
			empresarial: 'empresarial',
		},
		cardDescription:
			'Para la comunidad AG Start. heru declara tus impuestos, factura por ti y te respalda con expertos fiscales.',
		orgName: 'AG Enterprises',
		content: {
			plataformas: {
				label: 'socio de plataformas',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'Llegas con AG Start. heru declara los impuestos de tus plataformas cada mes para que tú sigas generando.',
				modules: [
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos con tu e.firma, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus plataformas, cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, internet… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			independiente: {
				label: 'profesionista independiente',
				regime: 'RESICO — Régimen Simplificado de Confianza',
				heroLine:
					'Llegas con AG Start. heru presenta tus declaraciones y factura por ti, para que te dediques a tu negocio.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tu declaración RESICO cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones por honorarios y servicios.',
					},
					{
						icon: 'CheckCircle',
						title: 'Alta y régimen sin vueltas al SAT',
						copy: 'Te ayudamos con tu alta en RESICO, e.firma y constancia para facturar desde ya.',
					},
					{
						icon: 'Files',
						title: 'Factura a tus clientes en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Equipo, internet, formación… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			empresarial: {
				label: 'dueño de negocio',
				regime: 'Actividad Empresarial y Profesional',
				heroLine:
					'Llegas con AG Start. heru lleva la contabilidad fiscal de tu negocio: declaraciones, facturas y deducciones al día.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Declaraciones de tu actividad cada mes',
						copy: 'Un contador calcula tus utilidades y presenta tus declaraciones de Actividad Empresarial o RIF.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Conciliamos ingresos y gastos',
						copy: 'Cruzamos tus facturas del SAT con tus gastos deducibles para optimizar tu carga fiscal.',
					},
					{
						icon: 'Files',
						title: 'Facturación incluida',
						copy: 'Emite CFDI a tus clientes y administra tus comprobantes desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Asesoría de expertos',
						copy: 'Especialistas fiscales revisan tu caso y resuelven tus dudas por chat.',
					},
				],
			},
			default: {
				label: 'emprendedor',
				regime: 'RESICO — Régimen Simplificado de Confianza',
				heroLine:
					'Llegas con AG Start. heru presenta tus declaraciones y factura por ti, para que te dediques a tu negocio.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Tus declaraciones presentadas cada mes',
						copy: 'Contadores expertos calculan y presentan tus declaraciones ante el SAT.',
					},
					{
						icon: 'Files',
						title: 'Factura a tus clientes en segundos',
						copy: 'Emite CFDI desde la app, con timbrado al instante.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Equipo, internet, formación… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	boosmap: {
		id: 'boosmap',
		name: 'Boosmap',
		platformName: 'Boosmap',
		logo: 'boosmap',
		bg: '#40095C',
		socioLabel: 'repartidor',
		benefit:
			'Repartes con Boosmap. heru te ayuda a facturarle a Boosmap en segundos para cobrar, y presenta tus declaraciones en RESICO.',
		planLabel: 'Plan RESICO para repartidores de Boosmap',
		planId: 'profesionista',
		cardTitle: 'Plan RESICO',
		cardDescription:
			'Para repartidores de Boosmap. Factura para cobrar y deja tus declaraciones RESICO en manos de heru.',
		orgName: 'Boosmap',
		content: {
			default: {
				label: 'repartidor',
				regime: 'Régimen RESICO · mensajería',
				heroLine:
					'Factura a Boosmap en menos de 10 segundos para cobrar; nosotros declaramos tus ingresos en RESICO.',
				modules: [
					{
						icon: 'Files',
						title: 'Factura a Boosmap en menos de 10 segundos',
						copy: 'Emite tu CFDI al instante para que Boosmap te pague sin demoras.',
						featured: true,
					},
					{
						icon: 'Receipt',
						title: 'Declaramos tus impuestos en RESICO',
						copy: 'El régimen más óptimo para tus ingresos. Presentamos tu declaración cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, equipo de reparto… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para facturar y cobrar.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	deel: {
		id: 'deel',
		name: 'Deel',
		logo: 'deel',
		bg: '#000000',
		socioLabel: 'colaborador',
		benefit:
			'Como colaborador de Deel, heru presenta tus declaraciones mensuales automáticamente y te ayuda a cumplir con tus obligaciones fiscales sin complicaciones.',
		planLabel: 'Plan RESICO para colaboradores Deel',
		planId: 'profesionista',
		cardTitle: 'Plan RESICO',
		cardDescription: 'Para personas contratadas a través de Deel.',
		orgName: 'Deel',
		content: {
			default: {
				label: 'profesionista independiente',
				regime: 'Régimen RESICO · servicios profesionales',
				heroLine:
					'Emitimos las facturas de tus servicios y presentamos tu declaración en RESICO, el régimen más óptimo para ti.',
				modules: [
					{
						icon: 'Files',
						title: 'Emitimos las facturas de tus servicios',
						copy: 'Factura a tus clientes o plataformas en segundos; nosotros nos encargamos del timbrado.',
					},
					{
						icon: 'Receipt',
						title: 'Declaramos tus impuestos en RESICO',
						copy: 'El régimen más óptimo para tus ingresos. Presentamos tu declaración cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Equipo, internet, home office… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'CheckCircle',
						title: 'Tu declaración anual, incluida',
						copy: 'Preparamos y presentamos tu declaración anual sin costo extra.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	airbnb: {
		id: 'airbnb',
		name: 'Airbnb',
		platformName: 'Airbnb',
		logo: 'airbnb',
		bg: '#FF5A5F',
		socioLabel: 'anfitrión',
		benefit:
			'Como anfitrión de Airbnb, heru presenta tus declaraciones mensuales automáticamente y te ayuda a cumplir con tus obligaciones fiscales sin complicaciones.',
		planLabel: 'Plan RESICO para anfitriones Airbnb',
		planId: 'profesionista',
		cardTitle: 'Plan RESICO',
		cardDescription:
			'Para anfitriones de Airbnb. Llevamos al día tu fiscal por los ingresos de tus reservas para que sigas hospedando sin preocupaciones.',
		orgName: 'Airbnb',
		content: {
			default: {
				label: 'anfitrión',
				regime: 'Régimen RESICO · hospedaje',
				heroLine:
					'heru pone al día tus impuestos de anfitrión: declaramos, facturamos y deducimos por tu hospedaje.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos tus ingresos por hospedaje ante el SAT cada mes.',
					},
					{
						icon: 'Files',
						title: 'Factura a huéspedes y empresas',
						copy: 'Emite CFDI a quien lo pida, incluidas empresas y facturas de plataforma.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Deducimos limpieza, insumos y comisiones',
						copy: 'Detectamos los gastos de tu hospedaje que bajan tu impuesto.',
					},
					{
						icon: 'CheckCircle',
						title: 'Tu declaración anual, incluida',
						copy: 'Preparamos y presentamos tu declaración anual sin costo extra.',
					},
					{
						icon: 'DeviceMobile',
						title: 'Comparte tu página de facturación',
						copy: 'Enlace y QR para que tus huéspedes pidan factura sin escribirte.',
						featured: true,
					},
				],
			},
		},
	},
	didi: {
		id: 'didi',
		name: 'DiDi',
		platformName: 'DiDi',
		logo: 'didi',
		bg: '#F8673A',
		/**
		 * Programa de Activación Fiscal DiDi (ASAT, t-056). Requisitos del
		 * convenio: régimen 625, actividad de reparto, e.firma vigente, CSD
		 * vigente y elegibilidad de facturación a cuenta de terceros (fc3).
		 * DiDi contrata por cuenta propia y el repartidor es BENEFICIARIO (no
		 * paga la activación); la identificación es contra la LISTA de DiDi
		 * (verify-and-claim t-054) con el CORREO como key — decisión de Stiven
		 * 2026-09-01, cambiable por config sin retrabajo (`link.keyType`).
		 *
		 * El copy espeja el de Zubale (el aliado de cumplimiento más parecido)
		 * sobre tres pilares: alianza oficial · te ayudamos a registrarte y
		 * ponerte en orden para poder recibir más tipos de pedidos en DiDi · es
		 * importante completar todos los pasos. Nunca promete lo que el contrato
		 * no cubre.
		 *
		 * DiDi tiene última palabra sobre el wording que ve el usuario (punto 4.2
		 * del contrato). Feedback de Gerardo Meza (2026-09-10) aplicado: el flujo
		 * se plantea como BENEFICIO ("recibir más tipos de pedidos en DiDi"), no
		 * como obligación ni como "activación" que DiDi concede; "sin costo"
		 * destacado en login y correo. Tabla de revisión en
		 * ~/heru/tasks/t-056-copies-didi.md y ~/heru/tasks/t-168-auto-0914.md.
		 *
		 * DOS servicios bajo un solo aliado (t-076): el programa cubre SOLO a
		 * repartidores de Last Mile, pero `didi` sigue en el grid y un conductor de
		 * viajes también es socio de DiDi. La pregunta `didi_intent` del flow (rol
		 * de DiDi) decide: 'activation' = programa (esta entrada de compliance);
		 * 'declarations' = flujo estándar de aliado, el que `didi` tenía antes del
		 * programa (sin lista, sin T&Cs, sin CURP, sin hub). El deep-link
		 * `?partner=didi` pre-estampa 'activation'; SIN respuesta guardada el
		 * programa no aplica y toda entrada a DiDi (grid del onboarding, «Aliados
		 * oficiales», perfil, hito) pasa primero por la pregunta. Lo que varía por
		 * servicio vive llaveado por rol (roleBenefits, roleSocioLabels, content);
		 * lo que no (benefit, cardSubtitle) está redactado neutro para valer en
		 * ambos.
		 */
		roleAnswerKey: DIDI_INTENT_ANSWER_KEY,
		socioLabel: 'socio repartidor',
		roleSocioLabels: { declarations: 'socio' },
		tutorialVideo: {
			src: didiTutorialVideo,
			title: 'Mich te explica cómo funciona',
			subtitle: 'Video de 1 minuto y medio',
			avatar: didiTutorialAvatar,
			poster: didiTutorialPoster,
			roles: ['activation'],
		},
		benefit:
			'Como socio de DiDi tienes a heru, su aliado oficial en impuestos. Elige el servicio que necesitas: la activación fiscal del programa DiDi Last Mile, sin costo para ti, o el plan de declaraciones mensuales de heru.',
		roleBenefits: {
			activation:
				'Como socio repartidor de DiDi, heru te ayuda a registrarte ante el SAT y a poner en orden tu perfil fiscal para poder recibir más tipos de pedidos en DiDi. Es parte de la alianza oficial entre DiDi y heru y no tiene costo para ti.',
			declarations:
				'Como socio de DiDi, heru se encarga de tus declaraciones mensuales ante el SAT para que sigas generando ingresos sin interrupciones.',
		},
		planLabel: 'Plan Plataformas Digitales para socios de DiDi',
		planId: 'plataformas',
		cardSubtitle: 'Para socios de DiDi',
		cardDescription:
			'Para socios de DiDi. heru declara tus impuestos cada mes ante el SAT por tus ingresos en la plataforma para que sigas generando sin trabas.',
		orgName: 'Didi',
		compliance: {
			docType: LEGAL_DOCS.didi,
			intent: {
				programChoice: 'activation',
				planOnlyChoice: 'declarations',
			},
			fiscalValidationPartner: 'didi',
			loginTagline: 'Sin costo para socios repartidores elegibles',
			link: {
				keyType: 'email',
				title: 'Confirma tu cuenta de DiDi',
				badge: 'Sin costo para ti',
				body: 'Escribe el correo con el que te registraste en DiDi. Lo cruzamos con la lista oficial de DiDi para confirmar que formas parte del programa y empezar tu activación fiscal. No tiene ningún costo para ti.',
				notFound:
					'No encontramos este correo en la lista oficial de DiDi. Revisa que sea el mismo con el que te registraste en DiDi e inténtalo de nuevo. Si sigue sin aparecer, escríbenos a soporte.',
				alreadyClaimed:
					'Este correo ya está vinculado a otra cuenta de heru. Si crees que es un error, escríbenos a soporte.',
				unavailable:
					'No pudimos confirmar tu cuenta de DiDi en este momento. Inténtalo de nuevo en unos minutos.',
			},
			tcs: {
				title: 'Autoriza tu activación fiscal con DiDi',
				body: 'Al aceptar, autorizas a heru a realizar por ti los trámites necesarios ante el SAT y a compartir con DiDi tu información fiscal para tu activación. Léelos aquí mismo:',
			},
			hub: {
				heroCopy:
					'Te ayudamos a registrarte y a poner en orden tu perfil fiscal para poder recibir más tipos de pedidos en DiDi. Es importante completar todos los pasos.',
				stepsTitle: 'Tu activación fiscal con DiDi',
				completionNote:
					'Es importante completar los {total} pasos: tu habilidad para recibir todos los tipos de pedidos de DiDi depende de esto. Si te falta alguno, heru te ayuda a resolverlo.',
				curp: {
					title: 'Comparte tu CURP',
					doneTitle: 'CURP validado',
					description:
						'Con tu CURP validamos tu situación fiscal ante el SAT: es el primer paso de tu activación con DiDi. Puedes avanzar con los demás pasos mientras lo verificamos.',
					// Mismos requisitos que el diagnóstico de Antonia en la línea ASAT
					// (t-279): lo que dice el hub y lo que dice el WhatsApp coincide.
					profileTitle: 'Así está tu perfil fiscal',
					pendingStepsTitle: {
						one: 'Estás a tan solo 1 paso de estar en orden',
						other: 'Estás a tan solo {n} pasos de estar en orden',
					},
					profile: [
						{
							id: 'efirma-sat',
							title: 'e.firma activa y vigente',
							why: 'DiDi te solicita tener una e.firma activa y vigente ante el SAT.',
							verdict: 'curpEfirmaStatus',
							valueKey: 'curpEfirmaValidTo',
							resolve: 'efirma-sat',
							okCopy: 'Gracias a la validación por CURP identificamos que tienes tu e.firma vigente.',
							okCopyWithValue:
								'Gracias a la validación por CURP identificamos que tienes tu e.firma vigente hasta el {valor}.',
							warnCopy:
								'Gracias a la validación por CURP identificamos que tu e.firma no está vigente. Agenda tu cita en el SAT y heru te acompaña en el trámite.',
							unknownCopy:
								'La validación por CURP no nos confirmó tu e.firma. Si la tienes vigente, súbela a heru; si no, agenda tu cita en el SAT.',
						},
						{
							id: 'efirma-heru',
							title: 'Sube tu e.firma a heru',
							doneTitle: 'Tu e.firma está en heru',
							why: 'Al subir tu e.firma a heru somos capaces de asegurar que tu perfil fiscal cumpla con todas las condiciones para DiDi.',
							verdict: 'efirmaEnHeru',
							resolve: 'efirma-heru',
							okCopy: 'Ya tenemos tu e.firma en heru.',
							warnCopy:
								'Aún no tenemos tu e.firma en heru. Con ella dejamos tu perfil en línea con los requisitos de DiDi.',
						},
						{
							id: 'csd-sat',
							title: 'Sellos de facturación activos y vigentes',
							why: 'DiDi te solicita tener tus sellos de facturación activos y vigentes ante el SAT.',
							verdict: 'curpCsdStatus',
							valueKey: 'curpCsdValidTo',
							resolve: 'csd-sat',
							okCopy: 'Gracias a la validación por CURP identificamos que tus sellos de facturación están activos.',
							okCopyWithValue:
								'Gracias a la validación por CURP identificamos que tus sellos de facturación están activos hasta el {valor}.',
							warnCopy:
								'Gracias a la validación por CURP identificamos que no tienes sellos de facturación activos. Con tu e.firma los tramitamos por ti.',
							unknownCopy:
								'La validación por CURP no nos confirmó tus sellos. Con tu e.firma los revisamos y, si hace falta, los tramitamos por ti.',
							notice:
								'Con tu e.firma tramitamos tus sellos por ti. El SAT puede tardar hasta 72 horas en activarlos.',
						},
						{
							id: 'csd-heru',
							title: 'Sube tus sellos a heru',
							doneTitle: 'Tus sellos están en heru',
							why: 'Al subir tus sellos a heru somos capaces de timbrar las facturas de tus servicios en DiDi y asegurar que tu perfil fiscal cumpla con sus condiciones.',
							verdict: 'csdEnHeru',
							resolve: 'csd-heru',
							okCopy: 'Ya tenemos tus sellos de facturación en heru.',
							warnCopy:
								'Aún no tenemos tus sellos en heru. Los necesitamos para timbrar las facturas de tus servicios en DiDi.',
							notice:
								'Con tu e.firma tramitamos tus sellos por ti. El SAT puede tardar hasta 72 horas en activarlos.',
						},
						{
							id: 'regimen',
							title: 'Régimen de plataformas digitales registrado',
							why: 'DiDi te solicita tener el régimen de plataformas digitales registrado ante el SAT.',
							verdict: 'curpPlatecRegimeStatus',
							valueKey: 'curpRegimesLabel',
							resolve: 'sat-update',
							okCopy:
								'Gracias a la validación por CURP identificamos que ya estás en el régimen de plataformas digitales, el que pide DiDi.',
							warnCopy:
								'Gracias a la validación por CURP identificamos que aún no estás en el régimen de plataformas digitales, el que pide DiDi. heru lo actualiza por ti con tu e.firma.',
							warnCopyWithValue:
								'Gracias a la validación por CURP identificamos que hoy estás en {valor}. DiDi pide el régimen de plataformas digitales: heru lo actualiza por ti con tu e.firma.',
							unknownCopy:
								'La validación por CURP no nos confirmó tu régimen. Lo revisamos con tu e.firma.',
							notice: 'Actualizaremos tu régimen en las próximas 12 horas hábiles.',
						},
						{
							id: 'actividad',
							title: 'Actividad de reparto dada de alta',
							why: 'DiDi te solicita tener dada de alta la actividad de reparto ante el SAT.',
							verdict: 'repartoActivityStatus',
							resolve: 'sat-update',
							okCopy:
								'Gracias a la validación por CURP identificamos que ya tienes dada de alta la actividad de reparto ante el SAT.',
							warnCopy:
								'Gracias a la validación por CURP identificamos que aún no tienes dada de alta la actividad de reparto, que pide DiDi. heru la da de alta por ti con tu e.firma.',
							unknownCopy:
								'La validación por CURP no nos confirmó tu actividad de reparto. La revisamos con tu e.firma.',
							notice:
								'Daremos de alta tu actividad de reparto en las próximas 12 horas hábiles.',
						},
					],
					appointmentWhatsApp:
						'Hola equipo heru, soy repartidor DiDi y quisiera su ayuda para agendar mi cita en el SAT y tramitar mi e.firma',
				},
				efirmaWhy:
					'Tu e.firma es tu firma digital ante el SAT. Te la solicitamos ya que es con la misma que nos aseguramos de que tu perfil quede en línea con los requisitos de DiDi.',
				efirmaAttention:
					'Recibimos tu e.firma. Ahora actualizamos tu régimen y tu actividad de reparto ante el SAT en las próximas 12 horas hábiles.',
				certificate: {
					kind: 'didi_certificate',
					fileName: 'certificado-heru-didi.pdf',
					downloadLabel: 'Descargar mi certificado',
					readyTitle: 'Tu certificado para DiDi está listo',
					readyBody:
						'heru certifica que cumples con todos los requisitos fiscales que pide DiDi. Descárgalo y compártelo con DiDi cuando te lo pidan.',
				},
				helpWhatsApp: {
					withName:
						'Hola equipo heru, les escribe {nombre}, socio DiDi ({correo}) para recibir asesoría y ayuda con mi activación fiscal',
					withoutName:
						'Hola equipo heru, les escribe un socio DiDi ({correo}) para recibir asesoría y ayuda con mi activación fiscal',
				},
				efirmaHelpWhatsApp:
					'Hola equipo heru, soy repartidor DiDi y quisiera obtener su ayuda para recuperar los archivos de mi e.firma',
				sellosWhy:
					'Tus sellos de facturación o CSD son los certificados que te permiten timbrar facturas ante el SAT. Los necesitamos para poder timbrar las facturas de tus servicios en DiDi.',
				allianceBanner:
					'Gracias a la alianza oficial entre DiDi y heru, nos encargamos de tu alta en el régimen de plataformas, tu actividad de reparto, tu e.firma y tus sellos de facturación para que puedas recibir más tipos de pedidos en DiDi. Es importante completar todos los pasos.',
				disclaimer:
					'heru verifica y pone en orden tu perfil fiscal; contratar el plan es tu decisión y la activación de tu cuenta en la plataforma la decide DiDi de manera exclusiva.',
			},
		},
		content: {
			activation: DIDI_ACTIVATION_CONTENT,
			// El servicio de declaraciones es el flujo estándar de aliado que `didi`
			// tenía antes del programa: mismo pack de entonces, en voz neutra (un
			// conductor de viajes y un repartidor son ambos socios de DiDi).
			declarations: {
				label: 'socio de DiDi',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'heru declara los impuestos de tus ingresos en DiDi cada mes para que sigas generando sin trabas con el SAT.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus viajes o entregas, cada mes antes del día 17.',
					},
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, mantenimiento, celular… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			// Sin intención respondida el programa aplica (records previos al nodo
			// `didi_intent`, o el prestamp del deep-link aún en vuelo): mismo pack.
			default: DIDI_ACTIVATION_CONTENT,
		},
	},
	didi_food: {
		id: 'didi_food',
		name: 'DiDi Food',
		platformName: 'DiDi',
		logo: 'didi-food',
		bg: '#FF6400',
		socioLabel: 'repartidor',
		benefit:
			'Como repartidor de DiDi Food, heru presenta tus declaraciones mensuales automáticamente y te mantiene al día con el SAT por las entregas que haces.',
		planLabel: 'Plan Plataformas Digitales para repartidores DiDi Food',
		planId: 'plataformas',
		cardDescription:
			'Para repartidores de DiDi Food. heru declara tus impuestos cada mes ante el SAT por tus entregas para que sigas trabajando sin trabas.',
		orgName: 'DiDi Food',
		content: {
			default: {
				label: 'repartidor',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'heru declara los impuestos de tus entregas cada mes para que sigas repartiendo sin trabas con el SAT.',
				modules: [
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus entregas, cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, equipo de reparto… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	doctoralia: {
		id: 'doctoralia',
		name: 'Doctoralia',
		logo: 'doctoralia',
		bg: '#00A085',
		socioLabel: 'doctor',
		benefit:
			'Atiendes pacientes con Doctoralia. heru factura por ti, declara tus ingresos y te devuelve horas de administración cada mes.',
		planLabel: 'Plan RESICO para doctores de Doctoralia',
		planId: 'profesionista',
		cardTitle: 'Plan RESICO',
		cardDescription:
			'Para doctores de Doctoralia. heru factura, declara y optimiza tus impuestos por tus consultas.',
		orgName: 'Doctoralia',
		content: {
			default: {
				label: 'doctor',
				regime: 'Régimen RESICO · servicios médicos',
				heroLine:
					'Tu página de auto-facturación y tus declaraciones en orden; recupera hasta 8 horas de administración al mes.',
				modules: [
					{
						icon: 'DeviceMobile',
						title: 'Tu propia página de auto-facturación para tus clientes',
						copy: 'Comparte tu enlace y QR; tus pacientes se facturan solos. Recupera hasta 8 horas de administración mensualmente.',
						featured: true,
					},
					{
						icon: 'Receipt',
						title: 'Declaramos tus impuestos cada mes',
						copy: 'Presentamos tu declaración por tus consultas ante el SAT, automáticamente.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Equipo médico, consultorio, insumos… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'CheckCircle',
						title: 'Tu declaración anual, incluida',
						copy: 'Preparamos y presentamos tu declaración anual sin costo extra.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	kovi: {
		id: 'kovi',
		name: 'Kovi',
		logo: 'kovi',
		bg: '#FF355A',
		socioLabel: 'socio conductor',
		benefit:
			'Como socio conductor con Kovi, heru se encarga de tus declaraciones mensuales ante el SAT para que puedas seguir generando ingresos sin interrupciones.',
		planLabel: 'Plan Plataformas Digitales para socios Kovi',
		planId: 'plataformas',
		cardDescription:
			'Para socios conductores que rentan con Kovi. heru declara tus impuestos cada mes para que sigas operando sin trabas con el SAT.',
		orgName: 'Kovi',
		content: {
			default: {
				label: 'conductor que renta con Kovi',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'Rentas con Kovi y manejas en Uber, DiDi u otras plataformas; heru declara tus impuestos y se asegura de deducir la renta de tu auto.',
				modules: [
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus viajes en cualquier plataforma, cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Tu renta con Kovi es deducible',
						copy: 'Deducimos la renta de tu auto, gasolina y mantenimiento; nos aseguramos de incluirla para optimizar tus impuestos.',
						featured: true,
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	lalamove: {
		id: 'lalamove',
		name: 'Lalamove',
		platformName: 'Lalamove',
		logo: 'lalamove',
		bg: '#F16622',
		socioLabel: 'repartidor',
		benefit:
			'Conduces y repartes con Lalamove. heru declara los impuestos de tus entregas cada mes para que sigas generando sin trabas con el SAT.',
		planLabel: 'Plan Plataformas Digitales para repartidores de Lalamove',
		planId: 'plataformas',
		cardDescription:
			'Para repartidores de Lalamove. heru declara tus impuestos cada mes ante el SAT por tus entregas.',
		orgName: 'LalaMove',
		content: {
			default: {
				label: 'repartidor',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'heru declara los impuestos de tus entregas cada mes para que sigas repartiendo sin trabas con el SAT.',
				modules: [
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus entregas, cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, mantenimiento, celular… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	mercado_envios: {
		id: 'mercado_envios',
		name: 'Mercado Envíos',
		platformName: 'Mercado Envíos',
		logo: 'mercado-envios',
		bg: '#FFE600',
		socioLabel: 'repartidor',
		benefit:
			'Como repartidor de Mercado Envíos, heru presenta tus declaraciones mensuales automáticamente y te ayuda a cumplir con tus obligaciones fiscales sin complicaciones.',
		planLabel: 'Plan Plataformas Digitales para repartidores Mercado Envíos',
		planId: 'plataformas',
		cardDescription:
			'Para repartidores de Mercado Envíos. heru declara tus impuestos cada mes y optimiza tus deducciones por las entregas que haces.',
		orgName: 'Mercado-Envios',
		content: {
			default: {
				label: 'repartidor',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'heru declara los impuestos de tus entregas cada mes para que sigas repartiendo sin trabas con el SAT.',
				modules: [
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus entregas, cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, equipo de reparto… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	mercado_libre: {
		id: 'mercado_libre',
		name: 'Mercado Libre',
		platformName: 'Mercado Libre',
		logo: 'mercado-libre',
		bg: '#FFE600',
		socioLabel: 'vendedor',
		benefit:
			'Como vendedor de Mercado Libre, heru presenta tus declaraciones mensuales automáticamente y optimiza tus deducciones por tus ventas.',
		planLabel: 'Plan Plataformas Digitales para vendedores Mercado Libre',
		planId: 'plataformas',
		cardDescription:
			'Para vendedores en Mercado Libre. heru declara tus impuestos cada mes y optimiza tus deducciones por costos de venta y envíos.',
		orgName: 'Mercado Libre',
		content: {
			default: {
				label: 'vendedor',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'heru declara los impuestos de tus ventas y optimiza tus deducciones para que tu tienda siga creciendo.',
				modules: [
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus ventas, cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Comisiones, envíos, insumos y costos de venta que bajan tu impuesto.',
					},
					{
						icon: 'Files',
						title: 'Factura a tus compradores',
						copy: 'Emite CFDI a quien te lo pida por tus ventas, directo desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	morada_uno: {
		id: 'morada_uno',
		name: 'Morada Uno',
		logo: 'morada-uno',
		bg: '#742A81',
		roleAnswerKey: MORADA_ROLE_ANSWER_KEY,
		socioLabel: 'aliado',
		roleSocioLabels: { agente: 'agente', propietario: 'propietario' },
		benefit:
			'Morada Uno protege tus rentas y, junto con heru, también tu cumplimiento fiscal. Nos encargamos de los impuestos de agentes y propietarios para que rentar sea seguro para todos.',
		planLabel: 'Plan RESICO para la comunidad Morada Uno',
		planId: 'profesionista',
		cardTitle: 'Plan RESICO',
		cardSubtitle: 'Para la comunidad Morada Uno',
		cardDescription:
			'Nosotros declaramos tus impuestos ante el SAT cada mes y mantenemos tu perfil fiscal en orden mientras tú te dedicas a rentar y cerrar operaciones.',
		orgName: 'Morada Uno',
		trial: {
			campaign: 'morada_uno_trial',
			monthlyFreeMonths: 1,
			annualTotalMonths: 15,
		},
		content: {
			agente: {
				label: 'agente de bienes raíces',
				regime: 'Régimen RESICO · comisiones',
				heroLine:
					'Como agente inmobiliario, heru mantiene al día los impuestos de tus comisiones para que te dediques a cerrar tratos.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'heru calcula y presenta ante el SAT los impuestos de tus comisiones. Tú no mueves un dedo.',
					},
					{
						icon: 'Files',
						title: 'Factura a tus clientes en segundos',
						copy: 'Emite y timbra CFDI por tus comisiones desde el celular, sin sistemas complicados.',
					},
					{
						icon: 'DeviceMobile',
						title: 'Tu página de facturación personal',
						copy: 'Comparte un enlace y QR propios para que tus clientes pidan su factura solos.',
						featured: true,
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, comisiones… encontramos lo que puedes deducir para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
			propietario: {
				label: 'propietario',
				regime: 'Arrendamiento',
				heroLine:
					'heru declara y factura tus ingresos por renta para que rentar sea simple y sin sustos con el SAT.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Declaramos tu renta cada mes',
						copy: 'Presentamos tu declaración de arrendamiento ante el SAT, automáticamente.',
					},
					{
						icon: 'Files',
						title: 'Factura la renta a tus inquilinos',
						copy: 'Emite el CFDI de tus rentas al instante, mes con mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Deducimos predial, mantenimiento y más',
						copy: 'Aplicamos tus gastos deducibles para bajar el impuesto de tu arrendamiento.',
					},
					{
						icon: 'DeviceMobile',
						title: 'Comparte tu página de facturación',
						copy: 'Enlace y QR propios para que tus inquilinos soliciten su factura.',
						featured: true,
					},
					{
						icon: 'Headset',
						title: 'Contador a tu disposición',
						copy: 'Especialistas resuelven tus dudas de arrendamiento cuando las tengas.',
					},
				],
			},
		},
	},
	rappi: {
		id: 'rappi',
		name: 'Rappi',
		platformName: 'Rappi',
		logo: 'rappi',
		bg: '#FF441F',
		socioLabel: 'rappitendero',
		benefit:
			'Como rappitendero, heru gestiona tus impuestos de plataformas digitales, presenta tus declaraciones mensuales y optimiza tus deducciones.',
		planLabel: 'Plan Plataformas Digitales para rappitenderos',
		planId: 'plataformas',
		cardDescription:
			'Para rappitenderos. heru gestiona tus impuestos mensuales y optimiza tus deducciones por tus entregas.',
		orgName: 'Rappi',
		compliance: {
			hub: {
				heroCopy:
					'Verificamos tu cumplimiento fiscal para que sigas repartiendo en Rappi sin interrupciones.',
				stepsTitle: 'Tu certificado heru para Rappi',
				completionNote:
					'Cuando completes estos {total} pasos, tu cumplimiento fiscal quedará al día ante el SAT y tendrás tu certificado heru listo para descargar.',
				curp: {
					title: 'Comparte tu CURP',
					doneTitle: 'CURP validado',
					description:
						'Con tu CURP validamos tu situación fiscal ante el SAT: es el primer paso para tu certificado heru. Puedes avanzar con los demás pasos mientras lo verificamos.',
					profileTitle: 'Así está tu perfil fiscal',
					pendingStepsTitle: {
						one: 'Estás a tan solo 1 paso de estar en orden',
						other: 'Estás a tan solo {n} pasos de estar en orden',
					},
					profile: [
						{
							id: 'rfc',
							title: 'RFC activo ante el SAT',
							why: 'Rappi te pide tener tu RFC activo ante el SAT para seguir repartiendo.',
							verdict: 'curpRfcStatus',
							valueKey: 'curpRfc',
							resolve: 'sat-update',
							okCopy: 'Gracias a la validación por CURP identificamos que tu RFC está activo.',
							okCopyWithValue: 'Gracias a la validación por CURP identificamos que tu RFC {valor} está activo.',
							warnCopy: 'Gracias a la validación por CURP identificamos que tu RFC no está activo ante el SAT. Con tu e.firma revisamos qué trámite hace falta y te acompañamos.',
							unknownCopy: 'La validación por CURP no nos confirmó tu RFC. Lo revisamos con tu e.firma.',
							notice: 'Revisamos tu RFC con tu e.firma y te escribimos con el siguiente paso.',
						},
						{
							id: 'csd-sat',
							title: 'Sellos de facturación activos y vigentes',
							why: 'Rappi te pide tener tus sellos de facturación activos y vigentes ante el SAT.',
							verdict: 'curpCsdStatus',
							valueKey: 'curpCsdValidTo',
							resolve: 'csd-sat',
							okCopy: 'Gracias a la validación por CURP identificamos que tus sellos de facturación están activos.',
							okCopyWithValue: 'Gracias a la validación por CURP identificamos que tus sellos de facturación están activos hasta el {valor}.',
							warnCopy: 'Gracias a la validación por CURP identificamos que no tienes sellos de facturación activos. Con tu e.firma los tramitamos por ti.',
							unknownCopy: 'La validación por CURP no nos confirmó tus sellos. Con tu e.firma los revisamos y, si hace falta, los tramitamos por ti.',
							notice: 'Con tu e.firma tramitamos tus sellos por ti. El SAT puede tardar hasta 72 horas en activarlos.',
						},
						{
							id: 'csd-heru',
							title: 'Sube tus sellos a heru',
							doneTitle: 'Tus sellos están en heru',
							why: 'Al subir tus sellos a heru podemos timbrar las facturas de tus ingresos en Rappi y asegurar que tu perfil cumpla con lo que pide Rappi.',
							verdict: 'csdEnHeru',
							resolve: 'csd-heru',
							okCopy: 'Ya tenemos tus sellos de facturación en heru.',
							warnCopy: 'Aún no tenemos tus sellos en heru. Los necesitamos para timbrar las facturas de tus ingresos en Rappi.',
							notice: 'Con tu e.firma tramitamos tus sellos por ti. El SAT puede tardar hasta 72 horas en activarlos.',
						},
					],
				},
				efirmaWhy:
					'Tu e.firma es tu firma digital ante el SAT. Con ella revisamos tu RFC y tramitamos tus sellos de facturación por ti.',
				sellosWhy:
					'Tus sellos de facturación o CSD son los certificados que te permiten timbrar facturas ante el SAT. Rappi te pide tenerlos activos y en heru para facturar tus ingresos como rappitendero.',
				allianceBanner:
					'Gracias a nuestra alianza con Rappi, en heru revisamos tu RFC y tramitamos tus sellos de facturación para que puedas seguir repartiendo en Rappi sin ningún problema.',
				certificate: {
					kind: 'rappi_certificate',
					fileName: 'certificado-heru-rappi.pdf',
					downloadLabel: 'Descargar Certificado heru',
					readyTitle: 'Tu certificado heru está listo',
					readyBody:
						'Cumples con todos los requisitos verificados por heru para generar ingresos como rappitendero. Descarga tu certificado y compártelo con Rappi.',
				},
				disclaimer:
					'Este certificado refleja tu cumplimiento verificado por heru; contratar el plan es tu decisión y la operación de tu cuenta en Rappi la decide Rappi de manera exclusiva.',
				partnerVerdictPartner: 'rappi',
				partnerVerdictCopy: 'Tu perfil fiscal está en orden en el programa de aliados de Rappi.',
				requiresEfirma: false,
			},
		},
		content: {
			default: {
				label: 'rappitendero',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'heru declara los impuestos de tus entregas cada mes y mantiene tu perfil al día para que sigas operando en Rappi.',
				modules: [
					{
						icon: 'Files',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus entregas, cada mes.',
					},
					{
						icon: 'CheckCircle',
						title: 'Tu certificado Rappi al día',
						copy: 'Mantenemos tu cumplimiento verificado para que sigas operando en la app.',
						featured: true,
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, equipo de reparto… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	terapify: {
		id: 'terapify',
		name: 'Terapify',
		platformName: 'Terapify',
		logo: 'terapify',
		bg: '#0085FF',
		socioLabel: 'psicólogo',
		benefit:
			'Das terapia a través de Terapify. heru declara los impuestos de tus sesiones cada mes para que te dediques a tus pacientes, no al SAT.',
		planLabel: 'Plan Plataformas Digitales para psicólogos de Terapify',
		// Deal Terapify (2026-08-11): mes 1 gratis (promo global mensual) + 15%
		// los siguientes 5 meses (cupón TerapifyPartner, repeating x5) — NO el 25%
		// forever de los demás partners. Display-only: espeja el cupón del tier.
		planId: 'plataformas',
		cardDescription:
			'Para psicólogos de Terapify. heru declara tus impuestos cada mes ante el SAT por tus sesiones.',
		orgName: 'Terapify',
		content: {
			default: {
				label: 'psicólogo',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'Terapify ya retiene impuestos de tus sesiones; heru los declara cada mes para que sigas atendiendo pacientes sin trabas con el SAT.',
				modules: [
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus sesiones, cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Consultorio, internet, formación… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	uber: {
		id: 'uber',
		name: 'Uber',
		platformName: 'Uber',
		logo: 'uber',
		bg: '#000000',
		socioLabel: 'socio conductor',
		benefit:
			'Como socio conductor de Uber, heru presenta tus declaraciones mensuales automáticamente para que cumplas con el SAT sin complicaciones.',
		planLabel: 'Plan Plataformas Digitales para socios Uber',
		planId: 'plataformas',
		cardDescription:
			'Para socios conductores de Uber. heru declara tus impuestos cada mes ante el SAT por tus viajes para que sigas manejando sin trabas.',
		orgName: 'Uber',
		content: {
			default: {
				label: 'socio conductor',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'heru declara los impuestos de tus viajes cada mes para que sigas manejando sin trabas con el SAT.',
				modules: [
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus viajes, cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, mantenimiento, celular… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	uber_eats: {
		id: 'uber_eats',
		name: 'Uber Eats',
		platformName: 'Uber',
		logo: 'uber-eats',
		bg: '#06C167',
		socioLabel: 'repartidor',
		benefit:
			'Como repartidor de Uber Eats, heru presenta tus declaraciones mensuales automáticamente para que cumplas con el SAT sin complicaciones.',
		planLabel: 'Plan Plataformas Digitales para repartidores Uber Eats',
		planId: 'plataformas',
		cardDescription:
			'Para repartidores de Uber Eats. heru declara tus impuestos cada mes ante el SAT por tus entregas para que sigas trabajando sin trabas.',
		orgName: 'Uber Eats',
		content: {
			default: {
				label: 'repartidor',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'heru declara los impuestos de tus entregas cada mes para que sigas repartiendo sin trabas con el SAT.',
				modules: [
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus entregas, cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, equipo de reparto… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	walmart: {
		id: 'walmart',
		name: 'Walmart',
		platformName: 'Walmart',
		logo: 'walmart',
		bg: '#0157E9',
		socioLabel: 'repartidor',
		benefit:
			'Repartes con Walmart. heru declara los impuestos de tus entregas cada mes para que sigas repartiendo sin trabas con el SAT.',
		planLabel: 'Plan Plataformas Digitales para repartidores de Walmart',
		planId: 'plataformas',
		cardDescription:
			'Para repartidores de Walmart. heru declara tus impuestos cada mes ante el SAT por tus entregas.',
		orgName: 'Walmart',
		content: {
			default: {
				label: 'repartidor',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'heru declara los impuestos de tus entregas cada mes para que sigas repartiendo sin trabas con el SAT.',
				modules: [
					{
						icon: 'CheckCircle',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar en plataformas.',
					},
					{
						icon: 'Receipt',
						title: 'Presentamos tu declaración mensual',
						copy: 'Calculamos y declaramos ante el SAT los impuestos de tus entregas, cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Gasolina, celular, equipo de reparto… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'Files',
						title: 'Facturas cuando las necesites',
						copy: 'Emite CFDI por tus ingresos y descarga tus constancias desde la app.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	workana: {
		id: 'workana',
		name: 'Workana',
		platformName: 'Workana',
		logo: 'workana',
		bg: '#1F79B8',
		socioLabel: 'freelancer',
		benefit:
			'Cobras tus proyectos a través de Workana. heru emite las facturas de tus servicios y presenta tus declaraciones en RESICO, el régimen más óptimo para ti.',
		planLabel: 'Plan RESICO para freelancers de Workana',
		planId: 'profesionista',
		cardTitle: 'Plan RESICO',
		cardDescription: 'Para freelancers que cobran a través de Workana.',
		orgName: 'Workana',
		content: {
			default: {
				label: 'freelancer',
				regime: 'Régimen RESICO · servicios profesionales',
				heroLine:
					'Emitimos las facturas de tus proyectos y presentamos tu declaración en RESICO, el régimen más óptimo para ti.',
				modules: [
					{
						icon: 'Files',
						title: 'Emitimos las facturas de tus servicios',
						copy: 'Factura tus proyectos en segundos; nosotros nos encargamos del timbrado.',
					},
					{
						icon: 'Receipt',
						title: 'Declaramos tus impuestos en RESICO',
						copy: 'El régimen más óptimo para tus ingresos. Presentamos tu declaración cada mes.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Equipo, internet, home office… aplicamos lo deducible para que pagues menos.',
					},
					{
						icon: 'CheckCircle',
						title: 'Tu declaración anual, incluida',
						copy: 'Preparamos y presentamos tu declaración anual sin costo extra.',
					},
					{
						icon: 'Headset',
						title: 'Un contador cuando lo necesites',
						copy: 'Resuelve tus dudas fiscales con especialistas por chat, sin agendar cita.',
					},
				],
			},
		},
	},
	zubale: {
		id: 'zubale',
		name: 'Zubale',
		platformName: 'Zubale',
		logo: 'zubale',
		bg: '#0043FC',
		socioLabel: 'Zubalero',
		benefit:
			'Como colaborador de Zubale, heru verifica y certifica tu perfil fiscal para que Zubale pueda liberar tus retiros. Sin cumplimiento fiscal, Zubale no puede pagarte.',
		planLabel: 'Plan Plataformas Digitales para Zubaleros',
		planId: 'plataformas',
		cardTitle: 'Plan Plataformas Digitales',
		cardSubtitle: 'Para Zubaleros',
		cardDescription:
			'Nosotros declaramos tus impuestos ante el SAT cada mes. Tú no haces nada y tu perfil fiscal se mantiene en orden.',
		cardBenefits: [
			'Te damos de alta en el SAT y en el régimen correcto, si aún no lo estás',
			'Presentamos tus declaraciones de IVA e ISR antes del día 17, cada mes',
			'Detectamos tus deducciones automáticamente para que pagues menos impuestos',
			'Contadores expertos te acompañan por correo cuando lo necesites',
		],
		orgName: 'Zubale',
		// Sin `link`: el Zubalero se autodeclara (no hay lista de Zubale que
		// cruzar). Mismo hub guiado que DiDi (t-338): la validación por CURP
		// atribuida a Zubale (bps partner 46) da los veredictos de sus cuatro
		// requisitos (RFC activo, e.firma, sellos y régimen 625).
		compliance: {
			docType: LEGAL_DOCS.zubale,
			fiscalValidationPartner: 'zubale',
			hub: {
				heroCopy:
					'Verificamos tu cumplimiento fiscal para que puedas seguir retirando en Zubale sin interrupciones.',
				stepsTitle: 'Tu certificado heru para Zubale',
				completionNote:
					'Cuando completes estos {total} pasos, tu cumplimiento fiscal quedará al día ante el SAT y tendrás tu certificado heru listo para descargar.',
				curp: {
					title: 'Comparte tu CURP',
					doneTitle: 'CURP validado',
					description:
						'Con tu CURP validamos tu situación fiscal ante el SAT: es el primer paso para tu certificado heru. Puedes avanzar con los demás pasos mientras lo verificamos.',
					profileTitle: 'Así está tu perfil fiscal',
					pendingStepsTitle: {
						one: 'Estás a tan solo 1 paso de estar en orden',
						other: 'Estás a tan solo {n} pasos de estar en orden',
					},
					profile: [
						{
							id: 'rfc',
							title: 'RFC activo ante el SAT',
							why: 'Zubale solo libera tus retiros si tu RFC está activo ante el SAT.',
							verdict: 'curpRfcStatus',
							valueKey: 'curpRfc',
							resolve: 'sat-update',
							okCopy: 'Gracias a la validación por CURP identificamos que tu RFC está activo.',
							okCopyWithValue: 'Gracias a la validación por CURP identificamos que tu RFC {valor} está activo.',
							warnCopy: 'Gracias a la validación por CURP identificamos que tu RFC no está activo ante el SAT. Con tu e.firma revisamos qué trámite hace falta y te acompañamos.',
							unknownCopy: 'La validación por CURP no nos confirmó tu RFC. Lo revisamos con tu e.firma.',
							notice: 'Revisamos tu RFC con tu e.firma y te escribimos con el siguiente paso.',
						},
						{
							id: 'efirma-sat',
							title: 'e.firma activa y vigente',
							why: 'Zubale te pide tener una e.firma activa y vigente ante el SAT.',
							verdict: 'curpEfirmaStatus',
							valueKey: 'curpEfirmaValidTo',
							resolve: 'efirma-sat',
							okCopy: 'Gracias a la validación por CURP identificamos que tienes tu e.firma vigente.',
							okCopyWithValue: 'Gracias a la validación por CURP identificamos que tienes tu e.firma vigente hasta el {valor}.',
							warnCopy: 'Gracias a la validación por CURP identificamos que tu e.firma no está vigente. Agenda tu cita en el SAT y heru te acompaña en el trámite.',
							unknownCopy: 'La validación por CURP no nos confirmó tu e.firma. Si la tienes vigente, súbela a heru; si no, agenda tu cita en el SAT.',
						},
						{
							id: 'efirma-heru',
							title: 'Sube tu e.firma a heru',
							doneTitle: 'Tu e.firma está en heru',
							why: 'Al subir tu e.firma a heru podemos asegurar que tu perfil fiscal cumpla con todo lo que pide Zubale.',
							verdict: 'efirmaEnHeru',
							resolve: 'efirma-heru',
							okCopy: 'Ya tenemos tu e.firma en heru.',
							warnCopy: 'Aún no tenemos tu e.firma en heru. Con ella dejamos tu perfil en línea con lo que pide Zubale.',
						},
						{
							id: 'csd-sat',
							title: 'Sellos de facturación activos y vigentes',
							why: 'Zubale te pide tener tus sellos de facturación activos y vigentes ante el SAT.',
							verdict: 'curpCsdStatus',
							valueKey: 'curpCsdValidTo',
							resolve: 'csd-sat',
							okCopy: 'Gracias a la validación por CURP identificamos que tus sellos de facturación están activos.',
							okCopyWithValue: 'Gracias a la validación por CURP identificamos que tus sellos de facturación están activos hasta el {valor}.',
							warnCopy: 'Gracias a la validación por CURP identificamos que no tienes sellos de facturación activos. Con tu e.firma los tramitamos por ti.',
							unknownCopy: 'La validación por CURP no nos confirmó tus sellos. Con tu e.firma los revisamos y, si hace falta, los tramitamos por ti.',
							notice: 'Con tu e.firma tramitamos tus sellos por ti. El SAT puede tardar hasta 72 horas en activarlos.',
						},
						{
							id: 'csd-heru',
							title: 'Sube tus sellos a heru',
							doneTitle: 'Tus sellos están en heru',
							why: 'Al subir tus sellos a heru podemos timbrar las facturas de tus ingresos en Zubale y asegurar que tu perfil cumpla con lo que pide Zubale.',
							verdict: 'csdEnHeru',
							resolve: 'csd-heru',
							okCopy: 'Ya tenemos tus sellos de facturación en heru.',
							warnCopy: 'Aún no tenemos tus sellos en heru. Los necesitamos para timbrar las facturas de tus ingresos en Zubale.',
							notice: 'Con tu e.firma tramitamos tus sellos por ti. El SAT puede tardar hasta 72 horas en activarlos.',
						},
						{
							id: 'regimen',
							title: 'Régimen de plataformas digitales registrado',
							why: 'Zubale te pide tener el régimen de plataformas digitales registrado ante el SAT.',
							verdict: 'curpPlatecRegimeStatus',
							valueKey: 'curpRegimesLabel',
							resolve: 'sat-update',
							okCopy: 'Gracias a la validación por CURP identificamos que ya estás en el régimen de plataformas digitales, el que pide Zubale.',
							warnCopy: 'Gracias a la validación por CURP identificamos que aún no estás en el régimen de plataformas digitales, el que pide Zubale. heru lo actualiza por ti con tu e.firma.',
							warnCopyWithValue: 'Gracias a la validación por CURP identificamos que hoy estás en {valor}. Zubale pide el régimen de plataformas digitales: heru lo actualiza por ti con tu e.firma.',
							unknownCopy: 'La validación por CURP no nos confirmó tu régimen. Lo revisamos con tu e.firma.',
							notice: 'Actualizaremos tu régimen en las próximas 12 horas hábiles.',
						},
					],
				},
				efirmaWhy:
					'Tu e.firma es tu firma digital ante el SAT. Te la solicitamos ya que es con la misma que nos aseguramos de que tu perfil quede en línea con lo que pide Zubale.',
				efirmaAttention:
					'Recibimos tu e.firma. Ahora revisamos tu RFC y tu régimen ante el SAT en las próximas 12 horas hábiles.',
				sellosWhy:
					'Tus sellos de facturación o CSD son los certificados que te permiten timbrar facturas ante el SAT. Los necesitamos para facturar tus ingresos como Zubalero.',
				allianceBanner:
					'Gracias a nuestra alianza con Zubale, en heru nos encargamos de configurar tu régimen fiscal y tramitar tus sellos de facturación para que puedas seguir trabajando como Zubalero sin ningún problema. Para activarlo, necesitas completar una tarea pendiente en Zubale.',
				certificate: {
					kind: 'zubale_certificate',
					fileName: 'certificado-heru-zubale.pdf',
					downloadLabel: 'Descargar Certificado heru',
					readyTitle: 'Tu certificado heru está listo',
					readyBody:
						'Cumples con todos los requisitos verificados por heru para generar ingresos como Zubalero. Descarga tu certificado y compártelo con Zubale.',
				},
				disclaimer:
					'Este certificado refleja tu cumplimiento verificado por heru; contratar el plan es tu decisión y la autorización de tus retiros la decide Zubale de manera exclusiva.',
				partnerVerdictPartner: 'zubale',
				partnerVerdictCopy: 'Tu perfil fiscal está en orden en el programa de aliados de Zubale.',
				partnerVerdictRelinkCopy:
					'Sube tu e.firma para confirmar tu cumplimiento con Zubale y descargar tu certificado.',
			},
		},
		content: {
			default: {
				label: 'Zubalero',
				regime: 'Régimen de plataformas tecnológicas (625)',
				heroLine:
					'Con tu plan activo, heru mantiene tu perfil fiscal en orden para que Zubale libere tus retiros sin fricción.',
				modules: [
					{
						icon: 'Receipt',
						title: 'Presentamos tus declaraciones de IVA e ISR',
						copy: 'Antes del día 17 de cada mes, sin que muevas un dedo.',
					},
					{
						icon: 'Files',
						title: 'e.firma, sellos y alta: te ayudamos con todo',
						copy: 'Te acompañamos a tramitar tu e.firma, sellos de facturación, constancia y tu alta ante el SAT. Todo lo que necesitas para cobrar como Zubalero.',
					},
					{
						icon: 'CheckCircle',
						title: 'Tu certificado Zubale vigente',
						copy: 'Tu cumplimiento verificado mantiene tus retiros fluyendo en Zubale.',
					},
					{
						icon: 'ChartLineUp',
						title: 'Detectamos tus deducciones',
						copy: 'Encontramos automáticamente lo que puedes deducir para que pagues menos impuestos.',
					},
					{
						icon: 'Headset',
						title: 'Contadores expertos te acompañan',
						copy: 'Resuelve tus dudas por correo o chat cuando lo necesites, sin agendar cita.',
					},
				],
			},
		},
	},
};

/** El aliado Zubale, para las pantallas propias de su branch (tcs, enrollment,
 * perfil) — en v2 hacían findAllyById('zubale'). */
export const ZUBALE_ALLY = ALLY_CATALOG.zubale;

/** Fallback de las miniaturas del mapa dev (sin answers no hay aliado). En el
 * flujo real el chrome solo se alcanza con ALLY_ANSWER_KEY ya respondida. */
const PREVIEW_FALLBACK_ALLY = ALLY_CATALOG.uber;

/** Aliado elegido según answers, con fallback estable para previews. */
export const resolveAlly = (answers: Record<string, AnswerValue>): Ally => {
	const id = answers[ALLY_ANSWER_KEY];
	if (typeof id !== 'string' || !isAllyId(id)) return PREVIEW_FALLBACK_ALLY;
	return ALLY_CATALOG[id];
};

/** Aliado con capacidad de CUMPLIMIENTO garantizada, para las pantallas del
 * flujo de compliance (ally-tcs / ally-profile). En el flow real esos nodos
 * solo se alcanzan con un aliado que declara `compliance`; el fallback a
 * Zubale cubre las miniaturas del mapa dev (answers vacíos → resolveAlly cae
 * en un aliado sin compliance y la pantalla no tendría qué pintar). */
export const resolveComplianceAlly = (
	answers: Record<string, AnswerValue>,
): Ally & { compliance: AllyCompliance } => {
	const ally = resolveAlly(answers);
	return ally.compliance !== undefined
		? (ally as Ally & { compliance: AllyCompliance })
		: (ALLY_CATALOG.zubale as Ally & { compliance: AllyCompliance });
};

/** Content pack del aliado + rol elegido (Morada Uno: answers['morada_role']):
 * el pack del rol cuando existe, el 'default' si no, y como red de seguridad el
 * primer pack del aliado. SIEMPRE regresa un pack — el compilador exige que cada
 * aliado tenga `content` no vacío, así que la pantalla nunca queda sin contenido. */
export const resolveAllyRoleContent = (
	ally: Ally,
	role: AnswerValue | undefined,
): AllyRoleContent => {
	const byRole = typeof role === 'string' ? ally.content[role] : undefined;
	return byRole ?? ally.content.default ?? Object.values(ally.content)[0];
};

/** Rol elegido para ESTE aliado: la respuesta de su propio nodo de rol
 * (Ally.roleAnswerKey) y nada más. El record acumula respuestas de nodos que
 * el usuario visitó y abandonó (eligió Morada Uno, contestó su rol, volvió al
 * grid y eligió DiDi): leer "el primer rol que tenga valor" le colgaba a DiDi
 * el rol de Morada Uno y con él la lista del programa. Sin nodo de rol, o sin
 * respuesta: undefined. */
export const resolveAllyRoleAnswer = (
	ally: Ally,
	answers: Record<string, AnswerValue>,
): AnswerValue | undefined =>
	ally.roleAnswerKey ? answers[ally.roleAnswerKey] : undefined;

/** Hero copy del aliado para el rol elegido: roleBenefits[rol] cuando la
 * promesa varía con la elección (DiDi), si no `benefit`. */
export const resolveAllyBenefit = (
	ally: Ally,
	role: AnswerValue | undefined,
): string =>
	(typeof role === 'string' ? ally.roleBenefits?.[role] : undefined) ??
	ally.benefit;

/** ¿El programa de cumplimiento del aliado APLICA a este usuario? La única
 * lectura legítima de `Ally.compliance` fuera de las pantallas propias del
 * programa (ally-tcs / ally-profile, que el flow ya rutea): con `intent`, el
 * programa aplica SOLO cuando el rol elegido (resolveAllyRoleAnswer) es
 * `programChoice` (DiDi: 'activation'); 'declarations' o sin respuesta = flujo
 * estándar de aliado, sin lista ni T&Cs ni hub. Sin `intent`, aplica siempre
 * (Zubale). Espejo exacto del visibleWhen `eq programChoice` de los nodos del
 * programa en el flow (paridad en flowData.test). */
export const resolveAllyCompliance = (
	ally: Ally,
	role: AnswerValue | undefined,
): AllyCompliance | undefined => {
	const compliance = ally.compliance;
	if (!compliance?.intent) return compliance;
	return role === compliance.intent.programChoice ? compliance : undefined;
};

/** Plan del aliado para el rol elegido: rolePlanIds decide cuando el convenio
 * cubre varias familias; sin rol (o sin mapa) manda planId. */
export const resolveAllyPlanId = (
	ally: Ally,
	role: AnswerValue | undefined,
): MonthlyPlanId =>
	(typeof role === 'string' ? ally.rolePlanIds?.[role] : undefined) ??
	ally.planId;

/** ¿El link de campaña de este aliado entra DIRECTO a su alianza (sin intro
 * genérica, sin «atrás» al grid, con la org ligada al entrar)? Todo aliado de
 * link exclusivo; uno del grid, solo si lo declara. Única lectura de las dos
 * banderas. */
export const hasDirectCampaignLanding = (ally: Ally): boolean =>
	ally.campaignOnly === true || ally.directCampaignLanding === true;

/** Interpola `{ally}` en el copy del nodo con el nombre del aliado elegido. */
export const applyAllyCopy = (text: string, ally: Ally): string =>
	text.replaceAll('{ally}', ally.name);

// ── Tema de marca (deriva TODO del hex `bg` del catálogo) ──

export type AllyTheme = {
	heroTextColor: string;
	heroSubTextColor: string;
	badgeBg: string;
	badgeBorder: string;
	ctaTextColor: string;
	background: string;
};

const hexLuminance = (hex: string): number => {
	const clean = hex.replace('#', '');
	const r = parseInt(clean.slice(0, 2), 16);
	const g = parseInt(clean.slice(2, 4), 16);
	const b = parseInt(clean.slice(4, 6), 16);
	return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

/** Accent para UI pequeña de marca (iconos, checks, pills) renderizada SOBRE
 * BLANCO. Los colores de marca claros (amarillo Mercado #FFE600) fallan
 * contraste ahí, así que se mezclan hacia negro conservando el matiz; los
 * oscuros pasan sin cambio. Los FONDOS tonales siguen usando el hex crudo — solo
 * los accents de primer plano pasan por aquí (paridad con el getAccessibleAccent
 * del allyTheme v2). */
export const getAccessibleAccent = (bg: string): string =>
	hexLuminance(bg) > 0.7 ? `color-mix(in srgb, ${bg} 45%, black)` : bg;

/** Contraste por luminancia del color de marca (Mercado Libre amarillo pide
 * texto oscuro; Deel negro pide blanco) — paridad con el allyTheme de v2. */
export const getAllyTheme = (ally: Ally): AllyTheme => {
	const needsDarkText = hexLuminance(ally.bg) > 0.7;
	return {
		heroTextColor: needsDarkText ? 'text-h-text-heading' : 'text-white',
		heroSubTextColor: needsDarkText ? 'text-h-text-medium' : 'text-white/80',
		badgeBg: needsDarkText ? 'bg-black/5' : 'bg-white/15',
		badgeBorder: needsDarkText ? 'border-black/5' : 'border-white/10',
		ctaTextColor: needsDarkText ? 'text-h-text-heading' : 'text-white',
		background: `linear-gradient(135deg, ${ally.bg} 0%, color-mix(in srgb, ${ally.bg} 85%, ${needsDarkText ? '#000' : '#fff'}) 100%)`,
	};
};
